// client/src/pages/Integrations.js
//
// ✅ CLIENT Integrations (Cphere)
// - Premium Cphere hero + poweredby header
// - Robust integrations fetch + OAuth banner support
// - ✅ Meta Ad Accounts auto-load reliably (no “must refresh”)
// - ✅ Reviewer-visible flow now requires an explicit saved Meta ad account before read verification
// - ✅ FIX: Google Analytics is now a REAL OAuth provider card with Connect / Disconnect
// - ✅ Google Analytics card placed after StackAdapt and before CinemaCloudWorks
//
// NOTE:
// - Uses auth token from localStorage("authToken")
// - Uses useAdvertiser() context for selected advertiser
//
// ✅ FIX (THIS UPDATE):
// - Prevents unsafe direct browser navigation to protected /authorize routes
// - OAuth start now requires JSON-mode authorize URL from backend
// - If backend does not return authorizeUrl, shows a clean UI error instead of landing on:
//   {"error":"Unauthorized: No token provided"}
// - Adds small provider-level loading state for better UX
//
// ✅ ENHANCEMENT (THIS UPDATE):
// - Client now supports JSON authorize start for:
//   META, TIKTOK, TIKTOK_LOGIN, X, GOOGLE, GOOGLE_ANALYTICS
//

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./Dashboard.css";
import "./Integrations.css";
import "./ProgrammaticAnalytics.css";
import CphereHeaderLogo from "../components/CphereHeaderLogo";
import YahooDspIntegrationCard from "../components/YahooDspIntegrationCard";
import {
  AdvertiserIdentityTrigger,
  GovernedAdvertiserSelectorModal,
} from "../components/GovernedAdvertiserSelector";
import useGovernedAdvertiserCatalog from "../hooks/useGovernedAdvertiserCatalog";
import { isAmazonMcpAdvertiserEnabled } from "../utils/amazonMcpReadiness";
import {
  formatAmazonDataThrough,
  formatAmazonReportingTime,
  readAmazonReporting,
} from "../utils/amazonReportingPresentation";
import { resolveOAuthReturnAdvertiserScope } from "../utils/oauthReturnAdvertiserScope";

// Platform logos
import metaLogo from "../assets/meta.jpg";
import tiktokLogo from "../assets/tiktok.jpg";
import xLogo from "../assets/x.jpg";
import googleLogo from "../assets/google.jpg";
import truthSocialLogo from "../assets/truth-social-logo.jpg";
import rumbleLogo from "../assets/rumble-logo.jpg";
import redditLogo from "../assets/reddit.jpg";
import amazonLogo from "../assets/amazon.jpg";
import cphereLogo from "../assets/cphere.png";
import googleAnalyticsLogo from "../assets/googleAnalytics.jpg";
import purpleFilmIcon from "../assets/PurpleFilmIcon.jpg";
import creativeStudioImage from "../assets/creativestudio.jpg";
import purpleMarbleImage from "../assets/PurpleLiquidMarble.jpg";
import metricoolLogo from "../assets/metricool.jpg";
import shopifyLogo from "../assets/shopify.png";
import linkedinLogo from "../assets/linkedin.png";
import aiOptimizationsImage from "../assets/aioptimizations.gif";
import accountTileImage from "../assets/settings.jpg";
import profileSettingsImage from "../assets/profilesettings.jpg";

import { useNavigate } from "react-router-dom";
import { useAdvertiser } from "../contexts/AdvertiserContext";

const DEFAULT_PROD_BACKEND = "https://cphere-backend.onrender.com";

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL ?? "";

const CLIENT_UI_ORIGIN_RAW =
  process.env.REACT_APP_CLIENT_URL ??
  "https://analytics.cphere.ai";

const SELECTED_ADV_ID_KEY = "cphereSelectedAdvertiserId";
const SELECTED_ADV_NAME_KEY = "cphereSelectedAdvertiserName";
const CL_AI_PROVIDER_KEY = "clSherlocAiProvider";

const readAiCapabilityStatus = (storageKey) => {
  let storedProvider = "";
  try {
    storedProvider = String(localStorage.getItem(storageKey) || "")
      .trim()
      .toLowerCase();
  } catch (_e) {
    storedProvider = "";
  }

  const isAuto = !storedProvider || storedProvider === "auto";
  return {
    sherloc:
      isAuto || ["chatgpt", "openai", "gpt", "sherloc"].includes(storedProvider),
    eden: isAuto || ["claude", "anthropic", "eden"].includes(storedProvider),
  };
};

const DEFAULT_INTEGRATION_STATE = {
  META: {
    provider: "META",
    label: "Meta",
    status: "not_connected",
    connectedAccount: null,
    lastSyncedAt: null,
    expiresAt: null,
  },
  TIKTOK: {
    provider: "TIKTOK",
    label: "TikTok",
    status: "not_connected",
    connectedAccount: null,
    lastSyncedAt: null,
    expiresAt: null,
    mapped: false,
    reportingReady: false,
    externalAccountId: null,
    mappingSource: null,
    reason: null,
  },
  TIKTOK_LOGIN: {
    provider: "TIKTOK_LOGIN",
    label: "TikTok Login",
    status: "not_connected",
    connectedAccount: null,
    lastSyncedAt: null,
  },
  X: {
    provider: "X",
    label: "X",
    status: "not_connected",
    connectedAccount: null,
    lastSyncedAt: null,
    expiresAt: null,
  },
  GOOGLE: {
    provider: "GOOGLE",
    label: "Google & YouTube",
    status: "not_connected",
    connectedAccount: null,
    lastSyncedAt: null,
    expiresAt: null,
  },
  GOOGLE_ANALYTICS: {
    provider: "GOOGLE_ANALYTICS",
    label: "Google Analytics",
    status: "not_connected",
    connectedAccount: null,
    lastSyncedAt: null,
    expiresAt: null,
  },
  STACKADAPT: {
    provider: "STACKADAPT",
    label: "CPHERE",
    scopeLabel: "Programmatic / Multi-Channel",
    status: "not_connected",
    connectedAccount: null,
    lastSyncedAt: null,
    expiresAt: null,
    connected: false,
    mapped: false,
    tokenPresent: false,
    tokenValid: false,
    advertiserMapped: false,
    advertiserTokenSaved: false,
    envTokenConfigured: false,
    envFallbackActive: false,
    credentialSource: null,
    tokenMasked: null,
    reportingReady: false,
    reportingQueryConfigured: false,
    campaignsQueryConfigured: false,
    accessLevel: "read_only",
    lastIngestedAt: null,
    lastSync: null,
    ingestStatus: "never",
    readinessNotes: [],
    error: null,
    connectionState: "not_connected",
    providerModel: "programmatic_multi_channel",
    channelScope: [],
    lifecycle: null,
  },
  TRUTH_SOCIAL: {
    provider: "TRUTH_SOCIAL",
    label: "Truth Social",
    status: "not_connected",
    connectedAccount: null,
    lastSyncedAt: null,
  },
  RUMBLE: {
    provider: "RUMBLE",
    label: "Rumble",
    status: "not_connected",
    connectedAccount: null,
    lastSyncedAt: null,
  },
  REDDIT: {
    provider: "REDDIT",
    label: "Reddit",
    status: "not_connected",
    connectedAccount: null,
    lastSyncedAt: null,
    expiresAt: null,
    scopes: [],
  },
  AMAZON: {
    provider: "AMAZON",
    label: "Amazon",
    status: "not_connected",
    connectedAccount: null,
    lastSyncedAt: null,
  },
  SHOPIFY: {
    provider: "SHOPIFY",
    label: "Shopify",
    status: "not_connected",
    connectedAccount: null,
    lastSyncedAt: null,
    readOnlyReady: false,
    reportingReady: false,
    accessLevel: "read_only",
    liveWritesEnabled: false,
    liveWritesDisabled: true,
    readiness: null,
  },
  LINKEDIN: {
    provider: "LINKEDIN",
    label: "LinkedIn Ads",
    status: "not_connected",
    connectedAccount: null,
    lastSyncedAt: null,
    readOnlyReady: false,
    reportingReady: false,
    accessLevel: "read_only",
    liveWritesEnabled: false,
    liveWritesDisabled: true,
    readiness: null,
  },
};

const META_LINKING_STATE_EMPTY = Object.freeze({
  setupRequired: false,
  manualSelectionRequired: false,
  autoLinked: false,
  alreadyLinked: false,
  eligibleManagedAccountCount: null,
  noEligibleManagedAccounts: false,
  mappingState: null,
  mappingSource: null,
  message: null,
});

const INTEGRATION_CARD_COPY = Object.freeze({
  META: "Campaigns and insights for reporting and AI analysis.",
  TIKTOK: "Ads reporting for analytics and AI insights.",
  TIKTOK_LOGIN: "Identity permissions for TikTok login and access flows.",
  X: "Reporting data for cross-platform analytics and visibility.",
  GOOGLE: "Google and YouTube reporting data for analytics.",
  REDDIT: "Reddit reporting data for cross-platform analytics.",
  AMAZON: "Amazon Ads connection and advertiser profile readiness.",
  STACKADAPT: "Programmatic multi-channel analytics across premium media.",
  GOOGLE_ANALYTICS: "Website and conversion reporting for GA4 analysis.",
  RUMBLE: "Ads reporting status for cross-platform analytics.",
  TRUTH_SOCIAL: "Ads reporting status for cross-platform analytics.",
  CINEMACLOUDWORKS: "Cinema reporting status for campaign analytics.",
  DROPBOX: "Creative Studio+ storage status for shared asset workflows.",
  GOOGLE_DRIVE: "Creative Studio storage status for shared assets.",
  METRICOOL: "Social analytics connection status for reporting.",
  SHERLOC: "AI Engine capability status for Sherloc Agentic-powered workflows.",
  EDEN: "AI Engine capability status for Eden COPILOT workflows.",
  QUANTUM: "Memory capability status for persistent deep learning campaign context.",
  SHOPIFY: "Read-only commerce reporting for orders, revenue, products, customers, inventory, discounts, and store readiness.",
  LINKEDIN: "Read-only LinkedIn Ads reporting, account visibility, campaign discovery, creative discovery, and analytics.",
});

const normalizeShopifyShopDomainForUi = (value) => {
  let text = String(value || "").trim().toLowerCase();
  text = text.replace(/^https?:\/\//, "");
  text = text.replace(/^admin\./, "");
  text = text.split("/")[0] || "";
  if (text && !text.includes(".")) text = `${text}.myshopify.com`;
  return text;
};

const isValidShopifyShopDomainForUi = (value) =>
  /^[a-z0-9][a-z0-9-]*\.myshopify\.com$/.test(
    normalizeShopifyShopDomainForUi(value)
  );

// "2026-02-05" → "Feb 5, 2026" (UTC-safe; empty string on bad input).
const formatMetaTestWindowDate = (dateOnly) => {
  const text = String(dateOnly ?? "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) return "";
  const parsed = new Date(`${text}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
};

// Detail line for the ads_read test window returned by the backend.
const describeMetaTestWindow = (testWindow) => {
  if (!testWindow || typeof testWindow !== "object") return "";
  if (testWindow.source === "imported_meta_campaign_flight") {
    const start = formatMetaTestWindowDate(testWindow.startDate);
    const end = formatMetaTestWindowDate(testWindow.endDate);
    return start && end
      ? `Test window: Imported Meta campaign flight, ${start} → ${end}`
      : "Test window: Imported Meta campaign flight";
  }
  if (testWindow.source === "default_verification_window") {
    return "Test window: Default ads_read verification window";
  }
  return testWindow.label ? `Test window: ${testWindow.label}` : "";
};

const formatMetaEvidenceMetric = (value, { currency = false, percent = false } = {}) => {
  if (value === null || value === undefined || value === "") {
    return "Not returned by Meta";
  }
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return String(value);
  if (currency) {
    return numeric.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }
  if (percent) return `${numeric.toFixed(2)}%`;
  return numeric.toLocaleString("en-US");
};

const formatMetaRetrievalTimestamp = (value) => {
  const date = new Date(value || "");
  if (Number.isNaN(date.getTime())) return "Not returned by Meta";
  return date.toLocaleString("en-US", {
    dateStyle: "medium",
    timeStyle: "long",
  });
};

// Pure Meta ad-account id helpers (module scope so hooks that use them don't
// need them as dependencies).
const normalizeMetaAdAccountId = (val) => {
  if (!val) return "";
  return String(val).trim().replace(/^act_/i, "");
};

const ensureActPrefix = (val) => {
  const clean = normalizeMetaAdAccountId(val);
  return clean ? `act_${clean}` : "";
};

const maskMetaAdAccountId = (val) => {
  const clean = normalizeMetaAdAccountId(val);
  return clean ? `act_••••${clean.slice(-4)}` : "";
};

const formatMaskedMetaAccount = (name, id) => {
  const accountName = String(name || "").trim();
  const maskedId = maskMetaAdAccountId(id);
  if (accountName && maskedId) return `${accountName} (${maskedId})`;
  return accountName || maskedId;
};

const normalizeCleanId = (val) => {
  if (!val) return "";
  return String(val).trim().replace(/^act_/i, "");
};

function Integrations() {
  const navigate = useNavigate();
  const { selectAdvertiser } = useAdvertiser();

  const [integrationStatus, setIntegrationStatus] = useState(
    DEFAULT_INTEGRATION_STATE
  );
  const [loadingIntegrations, setLoadingIntegrations] = useState(false);
  const [integrationError, setIntegrationError] = useState("");

  const [oauthBanner, setOauthBanner] = useState(null);
  const [integrationRefreshKey, setIntegrationRefreshKey] = useState(0);

  // ✅ make this STATE so effects rerun when integrations finish loading
  const [integrationsLoaded, setIntegrationsLoaded] = useState(false);

  // ✅ NEW: per-provider OAuth loading state
  const [oauthLoadingProvider, setOauthLoadingProvider] = useState(null);
  const [, setAmazonProfiles] = useState([]);
  const [, setAmazonSelectedProfileId] = useState("");
  const [amazonLoadingAction, setAmazonLoadingAction] = useState("");
  const [, setAmazonMessage] = useState("");
  const [, setAmazonError] = useState("");
  const [amazonProfilesLoadedKey, setAmazonProfilesLoadedKey] = useState("");
  const [shopifyShopDomainInput, setShopifyShopDomainInput] = useState("");

  const safeBase = (base) => String(base || "").replace(/\/+$/, "");

  const apiBase = useMemo(() => {
    const fromEnv = safeBase(API_BASE_URL);
    if (fromEnv) return fromEnv;

    const host = String(window.location.hostname || "").toLowerCase();
    const isHosted = host.includes("analytics.cphere.ai");

    return isHosted ? safeBase(DEFAULT_PROD_BACKEND) : "";
  }, []);

  useEffect(() => {
    const savedShopDomain = normalizeShopifyShopDomainForUi(
      integrationStatus?.SHOPIFY?.shopDomain ||
        integrationStatus?.SHOPIFY?.externalAccountId ||
        integrationStatus?.SHOPIFY?.connectedAccount ||
        ""
    );
    if (savedShopDomain && !shopifyShopDomainInput) {
      setShopifyShopDomainInput(savedShopDomain);
    }
  }, [
    integrationStatus?.SHOPIFY?.shopDomain,
    integrationStatus?.SHOPIFY?.externalAccountId,
    integrationStatus?.SHOPIFY?.connectedAccount,
    shopifyShopDomainInput,
  ]);

  const getAuthToken = () => localStorage.getItem("authToken") || "";

  const authHeaders = () => {
    const token = getAuthToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const providerSupportsJsonAuthorize = (provider) => {
    const p = String(provider || "").toUpperCase();
    return (
      p === "META" ||
      p === "TIKTOK" ||
      p === "TIKTOK_LOGIN" ||
      p === "X" ||
      p === "GOOGLE" ||
      p === "GOOGLE_ANALYTICS" ||
      p === "REDDIT" ||
      p === "AMAZON" ||
      p === "SHOPIFY" ||
      p === "LINKEDIN"
    );
  };

  // ============================================================
  // ✅ META
  // ============================================================
  const [metaAdAccounts, setMetaAdAccounts] = useState([]);
  const [metaSelectedAdAccountId, setMetaSelectedAdAccountId] = useState("");
  const [metaLoading, setMetaLoading] = useState(false);
  const [metaMessage, setMetaMessage] = useState("");
  // Detail line describing which insights window the ads_read test queried
  // (imported reporting campaign flight vs default window). Shown only with a
  // fresh test result.
  const [metaTestWindow, setMetaTestWindow] = useState("");
  const [metaReadResult, setMetaReadResult] = useState(null);
  const [metaError, setMetaError] = useState("");
  const [metaLinkingState, setMetaLinkingState] = useState(
    META_LINKING_STATE_EMPTY
  );

  const metaFetchAbortRef = useRef(null);
  const metaReqSeqRef = useRef(0);
  const integrationStatusAbortRef = useRef(null);
  const integrationStatusReqSeqRef = useRef(0);
  const amazonProfilesAbortRef = useRef(null);
  const amazonProfilesReqSeqRef = useRef(0);
  const advertiserScopeEpochRef = useRef(0);

  // ✅ Per-advertiser meta load lock. Marks load ATTEMPTS (success or error),
  // so a failed load never re-triggers the auto-load effect; only an explicit
  // Load/Refresh (force) or advertiser/connection change clears it.
  const [metaAccountsLoadedFor, setMetaAccountsLoadedFor] = useState(null);
  const [isFetchingMetaAccounts, setIsFetchingMetaAccounts] = useState(false);

  // ✅ Auto-config guard (save once per advertiser unless user changes)
  const [metaAutoConfiguredFor, setMetaAutoConfiguredFor] = useState(null);

  const [tiktokMappedAdvertiserId, setTiktokMappedAdvertiserId] = useState("");
  const [tiktokMappedAccountName, setTiktokMappedAccountName] = useState("");
  const [tiktokSavingMapping, setTiktokSavingMapping] = useState(false);
  const [tiktokMessage, setTiktokMessage] = useState("");
  const [tiktokError, setTiktokError] = useState("");

  // ============================================================
  // ✅ DISCONNECT MODAL
  // ============================================================
  const [disconnectModalOpen, setDisconnectModalOpen] = useState(false);
  const [pendingDisconnectProvider, setPendingDisconnectProvider] =
    useState(null);
  const [diagnosticsModalProvider, setDiagnosticsModalProvider] = useState(null);

  const clearAdvertiserScopedState = useCallback(
    (nextAdvertiser = null, { reason = "changed" } = {}) => {
      advertiserScopeEpochRef.current += 1;
      integrationStatusReqSeqRef.current += 1;
      amazonProfilesReqSeqRef.current += 1;
      metaReqSeqRef.current += 1;
      integrationStatusAbortRef.current?.abort();
      amazonProfilesAbortRef.current?.abort();
      metaFetchAbortRef.current?.abort();

      setIntegrationStatus(DEFAULT_INTEGRATION_STATE);
      setLoadingIntegrations(false);
      setIntegrationError("");
      setIntegrationsLoaded(false);
      setOauthBanner(null);
      setOauthLoadingProvider(null);

      setAmazonProfiles([]);
      setAmazonSelectedProfileId("");
      setAmazonLoadingAction("");
      setAmazonMessage("");
      setAmazonError("");
      setAmazonProfilesLoadedKey("");
      setShopifyShopDomainInput("");

      setMetaAccountsLoadedFor(null);
      setMetaAutoConfiguredFor(null);
      setMetaAdAccounts([]);
      setMetaSelectedAdAccountId("");
      setMetaLinkingState(META_LINKING_STATE_EMPTY);
      setMetaLoading(false);
      setIsFetchingMetaAccounts(false);
      setMetaMessage("");
      setMetaTestWindow("");
      setMetaReadResult(null);
      setMetaError("");

      setTiktokMappedAdvertiserId("");
      setTiktokMappedAccountName("");
      setTiktokSavingMapping(false);
      setTiktokMessage("");
      setTiktokError("");

      setDisconnectModalOpen(false);
      setPendingDisconnectProvider(null);
      setDiagnosticsModalProvider(null);

      try {
        const nextId = String(nextAdvertiser?.id || "").trim();
        const nextName = String(
          nextAdvertiser?.name || nextAdvertiser?.advertiser || ""
        ).trim();
        if (nextId && nextName) {
          sessionStorage.setItem(SELECTED_ADV_ID_KEY, nextId);
          sessionStorage.setItem(SELECTED_ADV_NAME_KEY, nextName);
        } else if (reason === "confirmed_removed") {
          sessionStorage.removeItem(SELECTED_ADV_ID_KEY);
          sessionStorage.removeItem(SELECTED_ADV_NAME_KEY);
        }
      } catch (_e) {}
    },
    []
  );

  const {
    selectedAdvertiserId: contextSelectedAdvertiserId,
    selectedAdvertiserName: contextSelectedAdvertiserName,
    visibleAdvertisers,
    currentAdvertiser,
    loading: advertiserCatalogLoading,
    error: advertiserCatalogError,
    errorMessage: advertiserCatalogErrorMessage,
    selectorDisabled,
    selectorEmpty,
    modalOpen: advertiserSelectorOpen,
    openSelector: openAdvertiserSelector,
    closeSelector: closeAdvertiserSelector,
    handleAdvertiserSelect,
  } = useGovernedAdvertiserCatalog({
    apiBase,
    role: "client",
    onAdvertiserChange: clearAdvertiserScopedState,
  });
  const selectedAdvertiserId =
    currentAdvertiser?.id == null ? "" : String(currentAdvertiser.id);
  const selectedAdvertiserName =
    currentAdvertiser?.name || contextSelectedAdvertiserName || "";
  const selectedAdvertiserObject =
    currentAdvertiser?.raw || currentAdvertiser || null;

  const openDisconnectModal = (provider) => {
    setPendingDisconnectProvider(provider);
    setDisconnectModalOpen(true);
  };

  const closeDisconnectModal = () => {
    setDisconnectModalOpen(false);
    setPendingDisconnectProvider(null);
  };

  const closeDiagnosticsModal = () => {
    setDiagnosticsModalProvider(null);
  };

  useEffect(() => {
    if (!disconnectModalOpen && !diagnosticsModalProvider) return undefined;

    const onKeyDown = (event) => {
      if (event.key !== "Escape") return;
      if (diagnosticsModalProvider) {
        closeDiagnosticsModal();
        return;
      }
      closeDisconnectModal();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [diagnosticsModalProvider, disconnectModalOpen]);

  const getClientReturnToOrigin = () => {
    const origin = safeBase(window.location.origin);
    const clientUi =
      safeBase(CLIENT_UI_ORIGIN_RAW) || "https://analytics.cphere.ai";

    if (origin && /(localhost|127\.0\.0\.1)/i.test(origin)) return origin;
    if (origin && origin.includes("analytics.cphere.ai")) return origin;
    return clientUi;
  };

  const providerPrettyName = (provider) => {
    const p = String(provider || "").toUpperCase();
    if (p === "META") return "Meta";
    if (p === "TIKTOK") return "TikTok";
    if (p === "TIKTOK_LOGIN") return "TikTok Login";
    if (p === "X") return "X";
    if (p === "GOOGLE") return "Google & YouTube";
    if (p === "GOOGLE_ANALYTICS") return "Google Analytics";
    if (p === "STACKADAPT") return "CPHERE";
    if (p === "TRUTH_SOCIAL") return "Truth Social";
    if (p === "RUMBLE") return "Rumble";
    if (p === "REDDIT") return "Reddit";
    if (p === "AMAZON") return "Amazon";
    return provider || "Integration";
  };

  const getIntegrationCardDescription = (key, fallback = "") => {
    const normalizedKey = String(key || "")
      .trim()
      .replace(/\s+/g, "_")
      .toUpperCase();
    return INTEGRATION_CARD_COPY[normalizedKey] || fallback;
  };

  const fetchJson = useCallback(async (url, options = {}) => {
    const res = await fetch(url, options);
    const text = await res.text().catch(() => "");
    let json = null;
    try {
      json = text ? JSON.parse(text) : null;
    } catch (_e) {
      json = null;
    }
    return { res, json, text };
  }, []);

  const fetchFirstOk = useCallback(async (candidates = [], options = {}) => {
    let last = null;
    for (const url of candidates) {
      try {
        const out = await fetchJson(url, options);
        last = { url, ...out };
        if (out?.res?.ok) return last;
      } catch (e) {
        last = {
          url,
          res: null,
          json: null,
          text: "",
          error: e?.message || String(e),
        };
      }
    }
    return last;
  }, [fetchJson]);

  const buildAmazonEndpoint = useCallback(
    (path, extraQuery = "") => {
      if (!apiBase || !selectedAdvertiserId) return "";
      const suffix = extraQuery ? `&${extraQuery}` : "";
      return `${apiBase}${path}?advertiserId=${encodeURIComponent(
        selectedAdvertiserId
      )}${suffix}&ts=${Date.now()}`;
    },
    [apiBase, selectedAdvertiserId]
  );

  const getAmazonProfileIdentifier = useCallback((profile) => {
    return String(
      profile?.profileId || profile?.profile_id || profile?.id || ""
    ).trim();
  }, []);

  // ✅ Restore advertiser selection after OAuth redirect
  useEffect(() => {
    if (contextSelectedAdvertiserId) return;

    try {
      const storedId = sessionStorage.getItem(SELECTED_ADV_ID_KEY);
      const storedName = sessionStorage.getItem(SELECTED_ADV_NAME_KEY);
      if (storedId && storedName) {
        selectAdvertiser({
          id: storedId,
          advertiser: storedName,
          name: storedName,
        });
      }
    } catch (_e) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ===== Derived statuses =====
  const stackAdaptConnected = useMemo(() => {
    if (!selectedAdvertiserObject) return false;
    const adv = selectedAdvertiserObject;
    return Boolean(
      adv.audioLink ||
        adv.audio ||
        adv.inGameLink ||
        adv.in_game_link ||
        adv.inGame ||
        adv.in_game ||
        adv.connectedLink ||
        adv.connectedTvLink ||
        adv.connectedTVLink ||
        adv.ctvLink ||
        adv.ctv ||
        adv.videoLink ||
        adv.video ||
        adv.mobileLink ||
        adv.mobile ||
        adv.displayLink ||
        adv.display ||
        adv.doohLink ||
        adv.dooh ||
        adv.emailLink ||
        adv.email_link ||
        adv.emailReportingLink ||
        adv.email_reporting_link ||
        adv.email
    );
  }, [selectedAdvertiserObject]);

  const cinemaCloudWorksConnected = useMemo(() => {
    if (!selectedAdvertiserObject) return false;
    const adv = selectedAdvertiserObject;
    return Boolean(
      adv.cinemaLink ||
        adv.cinema ||
        adv.cinemaCloudWorksLink ||
        adv.cinema_cloudworks_link
    );
  }, [selectedAdvertiserObject]);

  const truthSocialConnected = useMemo(() => {
    if (!selectedAdvertiserObject) return false;
    const adv = selectedAdvertiserObject;
    return Boolean(
      adv.truthSocialLink ||
        adv.truth_social_link ||
        adv.truthSocial ||
        adv.truth_social
    );
  }, [selectedAdvertiserObject]);

  const rumbleConnected = useMemo(() => {
    if (!selectedAdvertiserObject) return false;
    const adv = selectedAdvertiserObject;
    return Boolean(adv.rumbleLink || adv.rumble_link || adv.rumble);
  }, [selectedAdvertiserObject]);

  const dropboxConnected = useMemo(() => {
    if (!selectedAdvertiserObject) return false;
    const adv = selectedAdvertiserObject;
    const folderUrl =
      adv.dropboxFolderUrl ||
      adv.dropbox_folder_url ||
      adv.dropboxFolderURL ||
      adv.dropbox_folderURL ||
      "";
    return Boolean(
      String(folderUrl || "").trim() ||
        adv.dropboxLink ||
        adv.dropbox_link ||
        adv.dropbox
    );
  }, [selectedAdvertiserObject]);

  const googleDriveCreativeStudioConnected = useMemo(() => {
    if (!selectedAdvertiserObject) return false;
    const adv = selectedAdvertiserObject;

    const driveFolderUrl =
      adv.googleDriveFolderUrl ||
      adv.google_drive_folder_url ||
      adv.googleDriveFolderURL ||
      adv.google_drive_folderURL ||
      "";

    if (String(driveFolderUrl || "").trim()) return true;

    const providerRaw =
      adv.creativeStudioStorageProvider ||
      adv.creative_studio_storage_provider ||
      adv.creativeStorageProvider ||
      adv.creative_storage_provider ||
      adv.storageProvider ||
      adv.storage_provider ||
      "";

    const provider = String(providerRaw || "").toLowerCase();
    const providerIndicatesDrive =
      provider.includes("drive") ||
      provider.includes("google_drive") ||
      provider.includes("google drive") ||
      provider === "gdrive";

    const enabledFlag =
      adv.creativeStudioStorageEnabled ??
      adv.creative_studio_storage_enabled ??
      adv.creativeStudioEnabled ??
      adv.creative_studio_enabled ??
      adv.creativeStorageEnabled ??
      adv.creative_storage_enabled ??
      null;

    const folderId =
      adv.googleDriveFolderId ||
      adv.google_drive_folder_id ||
      adv.driveFolderId ||
      adv.drive_folder_id ||
      adv.creativeStudioDriveFolderId ||
      adv.creative_studio_drive_folder_id ||
      adv.creativeDriveFolderId ||
      adv.creative_drive_folder_id ||
      adv.creativeStudioRootFolderId ||
      adv.creative_studio_root_folder_id ||
      adv.creativeRootFolderId ||
      adv.creative_root_folder_id ||
      "";

    const driveLink =
      adv.googleDriveLink ||
      adv.google_drive_link ||
      adv.driveLink ||
      adv.drive_link ||
      adv.creativeStudioDriveLink ||
      adv.creative_studio_drive_link ||
      adv.creativeDriveLink ||
      adv.creative_drive_link ||
      "";

    const explicitHasStorage =
      adv.hasCreativeStudioStorage ??
      adv.has_creative_studio_storage ??
      adv.hasCreativeStorage ??
      adv.has_creative_storage ??
      null;

    if (explicitHasStorage === true) return true;

    const enabledTruthy =
      enabledFlag === true ||
      String(enabledFlag || "").toLowerCase() === "true" ||
      String(enabledFlag || "").toLowerCase() === "1";

    const hasFolderOrLink =
      Boolean(String(folderId || "").trim()) ||
      Boolean(String(driveLink || "").trim());

    if (providerIndicatesDrive && (enabledTruthy || hasFolderOrLink)) return true;
    if (hasFolderOrLink) return true;

    return false;
  }, [selectedAdvertiserObject]);

  const metricoolConnected = useMemo(() => {
    if (!selectedAdvertiserObject) return false;
    const adv = selectedAdvertiserObject;
    const url =
      adv.metricoolUrl ||
      adv.metricool_url ||
      adv.metricoolLink ||
      adv.metricool_link ||
      adv.metricool ||
      "";
    return Boolean(String(url || "").trim());
  }, [selectedAdvertiserObject]);

  const aiCapabilityStatus = readAiCapabilityStatus(CL_AI_PROVIDER_KEY);
  const sherlocConnected = aiCapabilityStatus.sherloc;
  // Temporary display alignment: Eden mirrors Sherloc connection state.
  const edenConnected = sherlocConnected;
  // Temporary display alignment: Quantum mirrors Sherloc connection state.
  const quantumMemoryConnected = sherlocConnected;

  // ============================
  // Fetch integrations
  // ============================
  useEffect(() => {
    if (!selectedAdvertiserId) {
      setIntegrationStatus(DEFAULT_INTEGRATION_STATE);
      setLoadingIntegrations(false);
      setIntegrationsLoaded(false);
      return undefined;
    }

    integrationStatusAbortRef.current?.abort();
    const controller = new AbortController();
    integrationStatusAbortRef.current = controller;
    const requestId = ++integrationStatusReqSeqRef.current;
    const scopeEpoch = advertiserScopeEpochRef.current;
    const requestAdvertiserId = selectedAdvertiserId;
    const requestIsCurrent = () =>
      !controller.signal.aborted &&
      requestId === integrationStatusReqSeqRef.current &&
      scopeEpoch === advertiserScopeEpochRef.current;

    const fetchIntegrationStatus = async () => {
      setLoadingIntegrations(true);
      setIntegrationError("");
      setIntegrationsLoaded(false);

      try {
        const token = getAuthToken();

        if (!apiBase) {
          setIntegrationStatus(DEFAULT_INTEGRATION_STATE);
          setIntegrationError("BACKEND URL NOT CONFIGURED.");
          return;
        }

        const primary = `${apiBase}/api/integrations/${encodeURIComponent(
          requestAdvertiserId
        )}?ts=${Date.now()}`;

        const fallback = `${apiBase}/api/oauth/integrations/${encodeURIComponent(
          requestAdvertiserId
        )}?ts=${Date.now()}`;

        const out = await fetchFirstOk([primary, fallback], {
          method: "GET",
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          credentials: "include",
          cache: "no-store",
          signal: controller.signal,
        });

        if (!requestIsCurrent()) return;
        let data = {};
        if (out?.res?.ok) data = out.json || {};

        setIntegrationStatus({
          META: {
            ...DEFAULT_INTEGRATION_STATE.META,
            ...(data.META || {}),
            label: DEFAULT_INTEGRATION_STATE.META.label,
          },
          TIKTOK: {
            ...DEFAULT_INTEGRATION_STATE.TIKTOK,
            ...(data.TIKTOK || {}),
            label: DEFAULT_INTEGRATION_STATE.TIKTOK.label,
          },
          TIKTOK_LOGIN: {
            ...DEFAULT_INTEGRATION_STATE.TIKTOK_LOGIN,
            ...(data.TIKTOK_LOGIN || {}),
            label: DEFAULT_INTEGRATION_STATE.TIKTOK_LOGIN.label,
          },
          X: {
            ...DEFAULT_INTEGRATION_STATE.X,
            ...(data.X || {}),
            label: DEFAULT_INTEGRATION_STATE.X.label,
          },
          GOOGLE: {
            ...DEFAULT_INTEGRATION_STATE.GOOGLE,
            ...(data.GOOGLE || {}),
            label: DEFAULT_INTEGRATION_STATE.GOOGLE.label,
          },
          GOOGLE_ANALYTICS: {
            ...DEFAULT_INTEGRATION_STATE.GOOGLE_ANALYTICS,
            ...(data.GOOGLE_ANALYTICS || {}),
            label: DEFAULT_INTEGRATION_STATE.GOOGLE_ANALYTICS.label,
          },
          STACKADAPT: {
            ...DEFAULT_INTEGRATION_STATE.STACKADAPT,
            ...(data.STACKADAPT || {}),
            label: DEFAULT_INTEGRATION_STATE.STACKADAPT.label,
          },
          TRUTH_SOCIAL: {
            ...DEFAULT_INTEGRATION_STATE.TRUTH_SOCIAL,
            ...(data.TRUTH_SOCIAL || {}),
            label: DEFAULT_INTEGRATION_STATE.TRUTH_SOCIAL.label,
          },
          RUMBLE: {
            ...DEFAULT_INTEGRATION_STATE.RUMBLE,
            ...(data.RUMBLE || {}),
            label: DEFAULT_INTEGRATION_STATE.RUMBLE.label,
          },
          REDDIT: {
            ...DEFAULT_INTEGRATION_STATE.REDDIT,
            ...(data.REDDIT || {}),
            label: DEFAULT_INTEGRATION_STATE.REDDIT.label,
          },
          AMAZON: {
            ...DEFAULT_INTEGRATION_STATE.AMAZON,
            ...(data.AMAZON || {}),
            label: DEFAULT_INTEGRATION_STATE.AMAZON.label,
          },
          SHOPIFY: {
            ...DEFAULT_INTEGRATION_STATE.SHOPIFY,
            ...(data.SHOPIFY || {}),
            label: DEFAULT_INTEGRATION_STATE.SHOPIFY.label,
          },
          LINKEDIN: {
            ...DEFAULT_INTEGRATION_STATE.LINKEDIN,
            ...(data.LINKEDIN || {}),
            label: DEFAULT_INTEGRATION_STATE.LINKEDIN.label,
          },
        });
      } catch (_err) {
        if (!requestIsCurrent()) return;
        setIntegrationStatus(DEFAULT_INTEGRATION_STATE);
        setIntegrationError(
          "UNABLE TO LOAD INTEGRATION STATUS. USING DEFAULT VIEW."
        );
      } finally {
        if (!requestIsCurrent()) return;
        setLoadingIntegrations(false);
        setIntegrationsLoaded(true);
      }
    };

    fetchIntegrationStatus();
    return () => {
      controller.abort();
      if (integrationStatusAbortRef.current === controller) {
        integrationStatusAbortRef.current = null;
      }
    };
  }, [selectedAdvertiserId, integrationRefreshKey, apiBase, fetchFirstOk]);

  const fetchAmazonProfiles = useCallback(
    async ({ silent = false } = {}) => {
      if (!selectedAdvertiserId || !apiBase) return [];

      amazonProfilesAbortRef.current?.abort();
      const controller = new AbortController();
      amazonProfilesAbortRef.current = controller;
      const requestId = ++amazonProfilesReqSeqRef.current;
      const scopeEpoch = advertiserScopeEpochRef.current;
      const requestAdvertiserId = selectedAdvertiserId;
      const requestIsCurrent = () =>
        !controller.signal.aborted &&
        requestId === amazonProfilesReqSeqRef.current &&
        scopeEpoch === advertiserScopeEpochRef.current;
      const token = getAuthToken();
      const url = buildAmazonEndpoint("/api/amazon/profiles");
      if (!url) return [];

      if (!silent) {
        setAmazonError("");
        setAmazonMessage("");
      }

      try {
        const { res, json, text } = await fetchJson(url, {
          method: "GET",
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          credentials: "include",
          cache: "no-store",
          signal: controller.signal,
        });

        if (!requestIsCurrent()) return [];
        if (!res?.ok || json?.ok === false) {
          throw new Error(
            json?.error ||
              json?.message ||
              text ||
              "FAILED TO LOAD AMAZON PROFILES."
          );
        }

        const profiles = Array.isArray(json?.profiles) ? json.profiles : [];
        setAmazonProfiles(profiles);
        setAmazonProfilesLoadedKey(String(requestAdvertiserId));
        setAmazonSelectedProfileId((current) => {
          const currentId = String(current || "").trim();
          if (
            currentId &&
            profiles.some(
              (profile) => getAmazonProfileIdentifier(profile) === currentId
            )
          ) {
            return currentId;
          }

          const mappedProfileId = String(
            integrationStatus?.AMAZON?.profileId ||
              integrationStatus?.AMAZON?.accountId ||
              ""
          ).trim();
          if (
            mappedProfileId &&
            profiles.some(
              (profile) =>
                getAmazonProfileIdentifier(profile) === mappedProfileId
            )
          ) {
            return mappedProfileId;
          }

          if (profiles.length === 1) {
            return getAmazonProfileIdentifier(profiles[0]);
          }

          return currentId && profiles.length ? currentId : "";
        });

        return profiles;
      } catch (error) {
        if (!requestIsCurrent() || error?.name === "AbortError") return [];
        throw error;
      } finally {
        if (amazonProfilesAbortRef.current === controller) {
          amazonProfilesAbortRef.current = null;
        }
      }
    },
    [
      apiBase,
      buildAmazonEndpoint,
      fetchJson,
      getAmazonProfileIdentifier,
      integrationStatus,
      selectedAdvertiserId,
    ]
  );

  useEffect(() => {
    if (!selectedAdvertiserId || !integrationsLoaded) return;

    const providerStatus = integrationStatus?.AMAZON || {};
    const amazonConnected = Boolean(
      providerStatus.connected ||
        providerStatus.status === "connected" ||
        providerStatus.connectedAccount
    );
    const amazonMapped = Boolean(
      providerStatus.mapped || providerStatus.lifecycle?.mapped
    );

    if (!amazonConnected || amazonMapped) return;
    if (amazonLoadingAction) return;
    if (amazonProfilesLoadedKey === String(selectedAdvertiserId)) return;

    let cancelled = false;

    fetchAmazonProfiles({ silent: true }).catch((error) => {
      if (cancelled) return;
      setAmazonError(error?.message || "FAILED TO LOAD AMAZON PROFILES.");
    });

    return () => {
      cancelled = true;
    };
  }, [
    amazonLoadingAction,
    amazonProfilesLoadedKey,
    fetchAmazonProfiles,
    integrationStatus,
    integrationsLoaded,
    selectedAdvertiserId,
  ]);

  const metaStoredAct = useMemo(() => {
    const ca = integrationStatus?.META?.connectedAccount || {};
    const fromIntegration =
      integrationStatus?.META?.adAccountId ||
      integrationStatus?.META?.metaAdAccountId ||
      integrationStatus?.META?.selectedAdAccountId ||
      ca?.adAccountId ||
      ca?.metaAdAccountId ||
      ca?.selectedAdAccountId ||
      ca?.id ||
      ca?.account_id ||
      ca?.ad_account_id ||
      "";
    return ensureActPrefix(fromIntegration || "");
  }, [integrationStatus]);

  const metaStoredName = useMemo(() => {
    const ca = integrationStatus?.META?.connectedAccount || {};
    const clean = normalizeCleanId(metaStoredAct);
    if (!clean) return "";

    const match = (metaAdAccounts || []).find((a) => {
      const rawId = String(a?.id || a?.account_id || a?.adAccountId || "");
      return normalizeCleanId(rawId) === clean;
    });

    return (
      integrationStatus?.META?.metaAdAccountName ||
      match?.name ||
      match?.account_name ||
      ca?.name ||
      ca?.account_name ||
      ""
    );
  }, [metaStoredAct, metaAdAccounts, integrationStatus]);

  const metaSelectionLabel = useMemo(() => {
    if (!metaStoredAct) return "Meta Ad Account: Not Configured";
    return formatMaskedMetaAccount(metaStoredName, metaStoredAct);
  }, [metaStoredAct, metaStoredName]);

  const metaHasSavedAccount = useMemo(
    () => Boolean(ensureActPrefix(metaStoredAct)),
    [metaStoredAct]
  );

  const metaHasPendingSelectionChange = useMemo(() => {
    const selected = ensureActPrefix(metaSelectedAdAccountId || "");
    const saved = ensureActPrefix(metaStoredAct || "");
    if (!selected || !saved) return false;
    return normalizeCleanId(selected) !== normalizeCleanId(saved);
  }, [metaSelectedAdAccountId, metaStoredAct]);

  const metaIsConnected =
    String(integrationStatus?.META?.status || "").toLowerCase() === "connected";

  // Precise Meta mapping-state copy (parity with superadmin). When Meta is
  // connected but no managed account is linked yet, describe the backend's
  // actual mapping verdict instead of a generic "Not Configured".
  const metaMappingStatusDisplay = useMemo(() => {
    const backendSource = String(
      integrationStatus?.META?.mappingSource || ""
    ).toLowerCase();
    if (backendSource && backendSource !== "missing") return null;
    if (!metaIsConnected) return null;

    const state = String(metaLinkingState.mappingState || "");

    if (state === "linked_from_managed_allowlist" || metaLinkingState.autoLinked) {
      return "Managed Account Auto-Link";
    }
    if (state === "managed_account_cross_advertiser_conflict") {
      return "Managed Account Mapped To Another Advertiser";
    }
    if (state === "managed_account_isolation_blocked") {
      return "Managed Account Isolation Blocked";
    }
    if (
      state === "no_returned_account_matches_managed_allowlist" ||
      metaLinkingState.noEligibleManagedAccounts
    ) {
      return "No Eligible Managed Account Returned";
    }
    if (
      state === "eligible_managed_account_found" ||
      (metaLinkingState.setupRequired &&
        (metaLinkingState.eligibleManagedAccountCount || 0) > 0)
    ) {
      return "Eligible Managed Account Found — Link Pending";
    }
    if (metaHasSavedAccount) return null;
    return "Not Linked Yet";
  }, [integrationStatus, metaIsConnected, metaLinkingState, metaHasSavedAccount]);

  // Stable highlight value for the Meta card: the linked account when saved,
  // otherwise the precise mapping state.
  const metaLinkedAccountDisplay = useMemo(() => {
    if (metaHasSavedAccount) return metaSelectionLabel;
    if (metaMappingStatusDisplay) {
      return `Meta Ad Account: ${metaMappingStatusDisplay}`;
    }
    return metaSelectionLabel;
  }, [metaHasSavedAccount, metaSelectionLabel, metaMappingStatusDisplay]);

  useEffect(() => {
    if (!metaIsConnected) return;
    if (!metaSelectedAdAccountId && metaStoredAct) {
      setMetaSelectedAdAccountId(metaStoredAct);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metaIsConnected, metaStoredAct]);

  const metaSelectedExistsInList = useMemo(() => {
    const pick = ensureActPrefix(metaSelectedAdAccountId || metaStoredAct || "");
    if (!pick) return false;
    const pickClean = normalizeCleanId(pick);

    return (metaAdAccounts || []).some((a) => {
      const rawId = String(a?.id || a?.account_id || a?.adAccountId || "");
      return normalizeCleanId(rawId) === pickClean;
    });
  }, [metaAdAccounts, metaSelectedAdAccountId, metaStoredAct]);

  useEffect(() => {
    const tiktokStatus = integrationStatus?.TIKTOK || {};
    setTiktokMappedAdvertiserId(String(tiktokStatus.externalAccountId || "").trim());
    setTiktokMappedAccountName(
      String(
        tiktokStatus.connectedAccount ||
          tiktokStatus.connectedAccountName ||
          selectedAdvertiserName ||
          ""
      ).trim()
    );
    setTiktokMessage("");
    setTiktokError("");
  }, [integrationStatus, selectedAdvertiserId, selectedAdvertiserName]);

  // ============================================================
  // ✅ META: Fetch ad accounts
  // ============================================================
  const fetchMetaAdAccounts = async ({ force = false } = {}) => {
    if (!selectedAdvertiserId) return;

    if (!apiBase) {
      setMetaAdAccounts([]);
      setMetaSelectedAdAccountId(metaStoredAct || "");
      setMetaLinkingState(META_LINKING_STATE_EMPTY);
      setMetaError("BACKEND URL NOT CONFIGURED.");
      return;
    }

    const advKey = String(selectedAdvertiserId);
    const scopeEpoch = advertiserScopeEpochRef.current;

    if (!force) {
      if (isFetchingMetaAccounts) return;
      if (metaAccountsLoadedFor === advKey && metaAdAccounts.length) return;
    }

    try {
      if (metaFetchAbortRef.current) metaFetchAbortRef.current.abort();
    } catch (_e) {}

    const controller = new AbortController();
    metaFetchAbortRef.current = controller;

    setIsFetchingMetaAccounts(true);
    setMetaLoading(true);
    setMetaError("");
    setMetaMessage("");

    const reqSeq = ++metaReqSeqRef.current;

    try {
      const token = getAuthToken();
      const portal = "client";
      const ts = Date.now();

      const candidates = [
        `${apiBase}/api/meta/adaccounts?advertiserId=${encodeURIComponent(
          advKey
        )}&ts=${ts}`,
        `${apiBase}/api/meta/adaccounts?advertiserId=${encodeURIComponent(
          advKey
        )}&portal=${encodeURIComponent(portal)}&ts=${ts}`,
        `${apiBase}/api/oauth/meta/adaccounts?advertiserId=${encodeURIComponent(
          advKey
        )}&ts=${ts}`,
        `${apiBase}/api/oauth/meta/adaccounts?advertiserId=${encodeURIComponent(
          advKey
        )}&portal=${encodeURIComponent(portal)}&ts=${ts}`,
      ];

      const out = await fetchFirstOk(candidates, {
        method: "GET",
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
        cache: "no-store",
        signal: controller.signal,
      });

      if (controller.signal.aborted) return;
      if (reqSeq !== metaReqSeqRef.current) return;
      if (scopeEpoch !== advertiserScopeEpochRef.current) return;

      if (!out?.res) {
        const reason =
          out?.error ||
          (out?.url ? `FAILED TO FETCH: ${out.url}` : "FAILED TO FETCH");
        // Mark the attempt even on failure so the auto-load effect does not
        // retry in a loop; only manual Load/Refresh (force) retries after an
        // error. The last linking summary is kept so the card stays stable.
        setMetaAccountsLoadedFor(advKey);
        setMetaAdAccounts([]);
        setMetaSelectedAdAccountId(ensureActPrefix(metaStoredAct) || "");
        setMetaError(
          `UNABLE TO LOAD META AD ACCOUNTS. ${String(reason).toUpperCase()}`
        );
        return;
      }

      const data = out.json || {};

      const errorish =
        data?.ok === false ||
        Boolean(data?.error) ||
        (typeof data?.message === "string" &&
          /error|token|unauthorized|invalid|missing/i.test(data.message));

      if (errorish) {
        const msg =
          data?.error ||
          data?.message ||
          out.text ||
          "META AD ACCOUNTS RESPONSE INDICATED AN ERROR.";
        setMetaAccountsLoadedFor(advKey);
        setMetaAdAccounts([]);
        setMetaSelectedAdAccountId(ensureActPrefix(metaStoredAct) || "");
        setMetaLinkingState({
          setupRequired: Boolean(data?.setupRequired),
          manualSelectionRequired: Boolean(data?.manualSelectionRequired),
          autoLinked: false,
          alreadyLinked: false,
          eligibleManagedAccountCount: Number.isFinite(
            Number(data?.eligibleManagedAccountCount)
          )
            ? Number(data.eligibleManagedAccountCount)
            : null,
          noEligibleManagedAccounts: Boolean(data?.noEligibleManagedAccounts),
          mappingState: data?.mappingState || null,
          mappingSource: data?.mappingSource || null,
          message: data?.message || null,
        });
        setMetaError(String(msg).toUpperCase());
        return;
      }

      if (out.res.status === 401 || out.res.status === 403) {
        const msg = data?.message || data?.error || out?.text || "UNAUTHORIZED";
        setMetaAccountsLoadedFor(advKey);
        setMetaAdAccounts([]);
        setMetaSelectedAdAccountId(ensureActPrefix(metaStoredAct) || "");
        setMetaError(
          `UNAUTHORIZED. PLEASE LOG IN AGAIN, THEN REFRESH. (${String(
            msg
          ).toUpperCase()})`
        );
        return;
      }

      if (!out.res.ok) {
        const msg =
          data?.message ||
          data?.error ||
          out?.text ||
          `${out.res.status} ${out.res.statusText}`;
        setMetaAccountsLoadedFor(advKey);
        setMetaAdAccounts([]);
        setMetaSelectedAdAccountId(ensureActPrefix(metaStoredAct) || "");
        setMetaError(
          `UNABLE TO LOAD META AD ACCOUNTS. ${String(msg).toUpperCase()}`
        );
        return;
      }

      const accounts =
        (Array.isArray(data?.data) && data.data) ||
        (Array.isArray(data?.accounts) && data.accounts) ||
        (Array.isArray(data?.adaccounts) && data.adaccounts) ||
        (Array.isArray(data?.result?.data) && data.result.data) ||
        (Array.isArray(data) && data) ||
        [];

      const ca = integrationStatus?.META?.connectedAccount || {};

      const backendSelected = ensureActPrefix(data?.selectedAdAccountId || "");
      const storedFromIntegration = ensureActPrefix(
        integrationStatus?.META?.adAccountId ||
          integrationStatus?.META?.metaAdAccountId ||
          integrationStatus?.META?.selectedAdAccountId ||
          ca?.adAccountId ||
          ca?.metaAdAccountId ||
          ca?.selectedAdAccountId ||
          ca?.id ||
          ca?.account_id ||
          ca?.ad_account_id ||
          ""
      );

      const preferred =
        backendSelected ||
        storedFromIntegration ||
        ensureActPrefix(metaSelectedAdAccountId) ||
        ensureActPrefix(metaStoredAct) ||
        "";

      const preferredClean = normalizeCleanId(preferred);
      const preferredExists =
        preferredClean &&
        accounts.some((a) => {
          const rawId = String(a?.id || a?.account_id || a?.adAccountId || "");
          return normalizeCleanId(rawId) === preferredClean;
        });

      let safeAccounts = accounts;

      if (!safeAccounts.length && preferred) {
        safeAccounts = [
          {
            id: preferred,
            name:
              metaStoredName ||
              ca?.name ||
              ca?.account_name ||
              "Meta Ad Account",
            __synthetic: true,
          },
        ];
      } else if (safeAccounts.length && preferred && !preferredExists) {
        safeAccounts = [
          ...safeAccounts,
          {
            id: preferred,
            name:
              metaStoredName ||
              ca?.name ||
              ca?.account_name ||
              "Selected Meta Ad Account",
            __synthetic: true,
          },
        ];
      }

      if (
        reqSeq !== metaReqSeqRef.current ||
        scopeEpoch !== advertiserScopeEpochRef.current
      ) {
        return;
      }

      setMetaAdAccounts(safeAccounts);
      setMetaAccountsLoadedFor(advKey);

      const eligibleManagedAccountCount = Number.isFinite(
        Number(data?.eligibleManagedAccountCount)
      )
        ? Number(data.eligibleManagedAccountCount)
        : accounts.length;
      const nextLinkingState = {
        setupRequired: Boolean(data?.setupRequired),
        manualSelectionRequired: Boolean(data?.manualSelectionRequired),
        autoLinked: Boolean(data?.autoLinked),
        alreadyLinked: Boolean(data?.alreadyLinked || data?.linkedAccount || data?.selected),
        eligibleManagedAccountCount,
        noEligibleManagedAccounts: Boolean(data?.noEligibleManagedAccounts),
        mappingState: data?.mappingState || null,
        mappingSource: data?.mappingSource || null,
        message: data?.message || null,
      };
      setMetaLinkingState(nextLinkingState);

      let first = "";
      if (safeAccounts.length) {
        const firstRaw =
          safeAccounts[0]?.id ||
          safeAccounts[0]?.account_id ||
          safeAccounts[0]?.adAccountId ||
          "";
        first = ensureActPrefix(firstRaw) || "";
      }

      const chosen = ensureActPrefix(preferred) || first || "";
      if (chosen) setMetaSelectedAdAccountId(chosen);

      if (data?.autoLinked) {
        setMetaAutoConfiguredFor(advKey);
        setMetaMessage(
          "META AD ACCOUNT LINKED. ADS_READ VERIFICATION IS NOW ENABLED FOR THIS ADVERTISER."
        );
        setIntegrationRefreshKey((k) => k + 1);
        return;
      }

      if (data?.noEligibleManagedAccounts) {
        const diag = data?.eligibilityDiagnostics || null;
        const noEligibleReason = data?.noEligibleReason || diag?.noEligibleReason || null;
        const diagSuffix = noEligibleReason
          ? ` (REASON: ${String(noEligibleReason)
              .replace(/_/g, " ")
              .toUpperCase()} — ${
              Number(diag?.metaAccountsReturnedCount ?? data?.accountsReturnedCount) || 0
            } ACCOUNT(S) RETURNED FROM META.)`
          : "";
        setMetaError(
          String(
            data?.message ||
              "NO ELIGIBLE CPHERE-MANAGED META AD ACCOUNTS ARE AVAILABLE FOR THIS ADVERTISER."
          ).toUpperCase() + diagSuffix
        );
        return;
      }

      if (data?.manualSelectionRequired) {
        setMetaMessage(
          "CHOOSE THE META AD ACCOUNT CPHERE SHOULD USE FOR THIS ADVERTISER, THEN CLICK LINK META AD ACCOUNT."
        );
        return;
      }

      const shouldWarnNoAccounts =
        integrationsLoaded && metaIsConnected && !accounts.length && !preferred;

      if (shouldWarnNoAccounts) {
        setMetaMessage(
          "NO META AD ACCOUNTS RETURNED. THIS USUALLY MEANS THE BACKEND DID NOT FIND A VALID META TOKEN FOR THIS ADVERTISER. TRY RECONNECTING META, THEN REFRESH."
        );
      }
    } catch (e) {
      if (String(e?.name || "") === "AbortError") return;
      if (reqSeq !== metaReqSeqRef.current) return;
      if (scopeEpoch !== advertiserScopeEpochRef.current) return;

      setMetaAccountsLoadedFor(advKey);
      setMetaError(
        String(e?.message || "UNABLE TO LOAD META AD ACCOUNTS.").toUpperCase()
      );
      setMetaAdAccounts([]);
      setMetaSelectedAdAccountId(ensureActPrefix(metaStoredAct) || "");
    } finally {
      if (reqSeq !== metaReqSeqRef.current) return;
      if (scopeEpoch !== advertiserScopeEpochRef.current) return;

      setIsFetchingMetaAccounts(false);
      setMetaLoading(false);
    }
  };

  const saveSelectedMetaAdAccount = async ({
    silent = false,
    adAccountId = "",
  } = {}) => {
    if (!selectedAdvertiserId || !apiBase) return false;

    const requestAdvertiserId = selectedAdvertiserId;
    const scopeEpoch = advertiserScopeEpochRef.current;
    const requestIsCurrent = () =>
      scopeEpoch === advertiserScopeEpochRef.current;
    const normalized = ensureActPrefix(
      adAccountId || metaSelectedAdAccountId || metaStoredAct
    );
    if (!normalized) {
      if (!silent) setMetaError("PLEASE SELECT A META AD ACCOUNT.");
      return false;
    }

    setMetaLoading(true);
    if (!silent) {
      setMetaError("");
      setMetaMessage("");
    }

    try {
      const token = getAuthToken();
      const portal = "client";

      const primary = `${apiBase}/api/meta/select-adaccount`;
      const fallback = `${apiBase}/api/oauth/meta/select-adaccount`;

      const out = await fetchFirstOk([primary, fallback], {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
        body: JSON.stringify({
          advertiserId: requestAdvertiserId,
          adAccountId: normalized,
          portal,
        }),
      });

      if (!requestIsCurrent()) return false;
      if (!out?.res) throw new Error(out?.error || "NO RESPONSE");

      const payload = out.json || {};
      if (!out.res.ok) {
        throw new Error(
          payload?.message ||
            payload?.error ||
            out.text ||
            "FAILED TO LINK META AD ACCOUNT."
        );
      }

      const saved = ensureActPrefix(payload?.meta?.adAccountId || normalized);
      setMetaSelectedAdAccountId(saved);

      if (!silent) {
        setMetaMessage(
          "META AD ACCOUNT LINKED. ADS_READ VERIFICATION IS NOW ENABLED FOR THIS ADVERTISER."
        );
      }
      setIntegrationRefreshKey((k) => k + 1);
      return true;
    } catch (e) {
      if (!requestIsCurrent()) return false;
      if (!silent) setMetaError(e?.message || "UNABLE TO LINK META AD ACCOUNT.");
      return false;
    } finally {
      if (requestIsCurrent()) setMetaLoading(false);
    }
  };

  const saveSelectedTikTokAdvertiser = async () => {
    if (!selectedAdvertiserId || !apiBase) return false;

    const requestAdvertiserId = selectedAdvertiserId;
    const scopeEpoch = advertiserScopeEpochRef.current;
    const requestIsCurrent = () =>
      scopeEpoch === advertiserScopeEpochRef.current;
    const externalAccountId = String(tiktokMappedAdvertiserId || "").trim();
    const connectedAccountName = String(tiktokMappedAccountName || "").trim();

    if (!externalAccountId) {
      setTiktokError("PLEASE ENTER A TIKTOK ADVERTISER ID.");
      return false;
    }

    setTiktokSavingMapping(true);
    setTiktokError("");
    setTiktokMessage("");

    try {
      const token = getAuthToken();
      const out = await fetch(`${apiBase}/api/oauth/tiktok/select-advertiser`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
        body: JSON.stringify({
          advertiserId: requestAdvertiserId,
          externalAccountId,
          connectedAccountName,
          portal: "client",
        }),
      });

      const payload = await out.json().catch(() => ({}));
      if (!requestIsCurrent()) return false;
      if (!out.ok) {
        throw new Error(
          payload?.error || payload?.message || "FAILED TO SAVE TIKTOK ADVERTISER MAPPING."
        );
      }

      setTiktokMappedAdvertiserId(String(payload?.externalAccountId || externalAccountId).trim());
      setTiktokMappedAccountName(
        String(payload?.connectedAccountName || connectedAccountName).trim()
      );
      setTiktokMessage(
        "TIKTOK ADVERTISER MAPPING SAVED. REPORTING WILL NOW USE THIS ADVERTISER AS THE ACCOUNT OF RECORD."
      );
      setIntegrationRefreshKey((k) => k + 1);
      return true;
    } catch (e) {
      if (!requestIsCurrent()) return false;
      setTiktokError(String(e?.message || "UNABLE TO SAVE TIKTOK ADVERTISER MAPPING."));
      return false;
    } finally {
      if (requestIsCurrent()) setTiktokSavingMapping(false);
    }
  };

  const runMetaReadTest = async () => {
    if (!selectedAdvertiserId || !apiBase) return;

    const requestAdvertiserId = selectedAdvertiserId;
    const scopeEpoch = advertiserScopeEpochRef.current;
    const requestIsCurrent = () =>
      scopeEpoch === advertiserScopeEpochRef.current;
    const savedAct = ensureActPrefix(metaStoredAct || "");
    if (!savedAct) {
      setMetaError(
        "LINK A META AD ACCOUNT FOR THIS ADVERTISER BEFORE RUNNING THE READ-ONLY VERIFICATION TEST."
      );
      setMetaMessage("");
      return;
    }

    if (metaHasPendingSelectionChange) {
      setMetaError(
        "THE SELECTED META AD ACCOUNT HAS NOT BEEN LINKED YET. CLICK LINK META AD ACCOUNT BEFORE RUNNING THE READ TEST."
      );
      setMetaMessage("");
      return;
    }

    setMetaLoading(true);
    setMetaError("");
    setMetaMessage("");
    setMetaTestWindow("");
    setMetaReadResult(null);

    try {
      const token = getAuthToken();
      const portal = "client";

      const primary = `${apiBase}/api/meta/read-test?advertiserId=${encodeURIComponent(
        requestAdvertiserId
      )}&portal=${encodeURIComponent(portal)}&ts=${Date.now()}`;

      const fallback = `${apiBase}/api/oauth/meta/read-test?advertiserId=${encodeURIComponent(
        requestAdvertiserId
      )}&portal=${encodeURIComponent(portal)}&ts=${Date.now()}`;

      const out = await fetchFirstOk([primary, fallback], {
        method: "GET",
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
        cache: "no-store",
      });

      if (!requestIsCurrent()) return;
      if (!out?.res) throw new Error(out?.error || "NO RESPONSE");

      const payload = out.json || {};
      if (!out.res.ok || payload?.ok === false) {
        throw new Error(
          payload?.message ||
            payload?.error ||
            out.text ||
            "META READ TEST FAILED."
        );
      }

      const i =
        payload?.insightsRows ?? payload?.counts?.insights ?? payload?.insightsCount ?? 0;
      const testWindow = payload?.testWindow || null;
      const insightsOk = payload?.insightsRead !== false;
      const performanceEvidence = payload?.performanceEvidence || null;
      const campaignPerformanceEvidence = Array.isArray(
        payload?.campaignPerformanceEvidence
      )
        ? payload.campaignPerformanceEvidence
        : [];
      const reviewerEvidenceSummary = payload?.reviewerEvidenceSummary || null;
      const reviewerEvidenceState = String(
        reviewerEvidenceSummary?.reviewerEvidenceState ||
          performanceEvidence?.reviewerEvidenceState ||
          "BLOCKED_BY_DATA_OR_ACCESS"
      );

      setMetaMessage(
        reviewerEvidenceState === "REVIEWER_READY"
          ? "META ADS_READ EVIDENCE COMPLETE — LIVE PERFORMANCE RETRIEVED ACROSS READABLE CAMPAIGNS."
          : "META ADS_READ CAMPAIGN READ VERIFIED — PERFORMANCE DATA NOT RETURNED."
      );

      const insightsDetail = insightsOk
        ? `Account-level campaign reporting query completed for the maximum available Meta range.${
            i > 0 ? ` ${i} row(s) returned.` : ""
          }`
        : "";
      const reviewCampaignDetail = payload?.reviewCampaignInsightsRead
        ? `The designated review campaign exact-window query returned ${Number(
            payload?.reviewCampaignInsightsRows || 0
          )} row(s).`
        : "";
      setMetaTestWindow(
        [insightsDetail, reviewCampaignDetail, describeMetaTestWindow(testWindow)]
          .filter(Boolean)
          .join(" ")
      );
      setMetaReadResult({
        linkedAdAccount:
          String(payload?.linkedAdAccount || "").trim() ||
          maskMetaAdAccountId(savedAct),
        campaignsCount: Number(
          payload?.campaignsCount ??
            payload?.campaignCount ??
            payload?.counts?.campaigns ??
            0
        ),
        insightsRows: Number(i || 0),
        insightsStatus: String(payload?.insightsStatus || "success_empty"),
        liveCampaigns: Array.isArray(payload?.liveCampaigns)
          ? payload.liveCampaigns
          : [],
        liveInsights: Array.isArray(payload?.liveInsights)
          ? payload.liveInsights
          : [],
        performanceEvidence,
        campaignPerformanceEvidence,
        reviewerEvidenceSummary,
      });
    } catch (e) {
      if (!requestIsCurrent()) return;
      setMetaError(e?.message || "META READ TEST FAILED.");
      setMetaTestWindow("");
      setMetaReadResult(null);
    } finally {
      if (requestIsCurrent()) setMetaLoading(false);
    }
  };

  // ============================================================
  // ✅ META AUTO-LOAD (RELIABLE)
  // ============================================================
  useEffect(() => {
    if (!selectedAdvertiserId) return;
    if (!apiBase) return;
    if (!integrationsLoaded) return;

    if (!metaIsConnected) {
      try {
        if (metaFetchAbortRef.current) metaFetchAbortRef.current.abort();
      } catch (_e) {}

      setMetaAccountsLoadedFor(null);
      setMetaAutoConfiguredFor(null);
      setMetaAdAccounts([]);
      setMetaSelectedAdAccountId("");
      setMetaLinkingState(META_LINKING_STATE_EMPTY);
      setMetaMessage("");
      setMetaTestWindow("");
      setMetaReadResult(null);
      setMetaError("");
      return;
    }

    // metaAccountsLoadedFor marks attempts (including failed ones), so this
    // effect runs at most one load per advertiser/connection; errors surface
    // once and only the Load/Refresh button (force) retries.
    const advKey = String(selectedAdvertiserId);
    if (metaAccountsLoadedFor === advKey) return;
    if (isFetchingMetaAccounts) return;

    fetchMetaAdAccounts({ force: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedAdvertiserId,
    apiBase,
    integrationsLoaded,
    metaIsConnected,
    metaAccountsLoadedFor,
    isFetchingMetaAccounts,
  ]);

  // ============================================================
  // ✅ META ACCOUNT LINKING REMINDER
  // ============================================================
  useEffect(() => {
    if (!selectedAdvertiserId) return;
    if (!metaIsConnected) return;
    if (!integrationsLoaded) return;
    if (!metaAdAccounts || !metaAdAccounts.length) return;

    const advKey = String(selectedAdvertiserId);
    if (metaAutoConfiguredFor === advKey) return;
    if (metaLinkingState.noEligibleManagedAccounts) return;

    const hasStored = Boolean(ensureActPrefix(metaStoredAct));
    if (hasStored || metaLinkingState.autoLinked || metaLinkingState.alreadyLinked) {
      setMetaAutoConfiguredFor(advKey);
      return;
    }

    if (metaLinkingState.manualSelectionRequired || metaAdAccounts.length > 1) {
      const firstRaw =
        metaAdAccounts[0]?.id ||
        metaAdAccounts[0]?.account_id ||
        metaAdAccounts[0]?.adAccountId ||
        "";
      const first = ensureActPrefix(firstRaw);

      if (first) setMetaSelectedAdAccountId(first);
      setMetaAutoConfiguredFor(advKey);
      setMetaMessage(
        "CHOOSE THE META AD ACCOUNT CPHERE SHOULD USE FOR THIS ADVERTISER, THEN CLICK LINK META AD ACCOUNT."
      );
      return;
    }

    const firstRaw =
      metaAdAccounts[0]?.id ||
      metaAdAccounts[0]?.account_id ||
      metaAdAccounts[0]?.adAccountId ||
      "";
    const first = ensureActPrefix(firstRaw);

    if (!first) {
      setMetaAutoConfiguredFor(advKey);
      return;
    }

    setMetaSelectedAdAccountId(first);
    setMetaAutoConfiguredFor(advKey);
    saveSelectedMetaAdAccount({ silent: true, adAccountId: first }).then((ok) => {
      if (!ok) return;
      setMetaMessage(
        "META AD ACCOUNT LINKED. ADS_READ VERIFICATION IS NOW ENABLED FOR THIS ADVERTISER."
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedAdvertiserId,
    metaIsConnected,
    integrationsLoaded,
    metaAdAccounts,
    metaStoredAct,
    metaAutoConfiguredFor,
    metaLinkingState,
  ]);

  // ✅ OAuth banner support
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const oauth = params.get("oauth");
    const provider = params.get("provider");
    const errorMsg = params.get("error");
    const status = params.get("status");
    const callbackAdvertiserId = String(
      params.get("advertiserId") ||
        params.get("advertiser_id") ||
        params.get("oauthAdvertiserId") ||
        ""
    ).trim();
    const hasOauthReturn = Boolean(oauth || provider || errorMsg || status);

    if (!hasOauthReturn) return;
    // Wait until the governed catalog has either confirmed the current
    // advertiser or failed closed. Applying callback state while authority is
    // still unknown can briefly paint a connection onto the wrong tenant.
    if (advertiserCatalogLoading) return;

    let sessionAdvertiserId = "";
    try {
      sessionAdvertiserId = String(
        sessionStorage.getItem(SELECTED_ADV_ID_KEY) || ""
      ).trim();
    } catch (_e) {
      sessionAdvertiserId = "";
    }
    const {
      returnAdvertiserId,
      callbackSessionMismatch,
      verified: resolvedOauthScope,
    } = resolveOAuthReturnAdvertiserScope({
      callbackAdvertiserId,
      sessionAdvertiserId,
      selectedAdvertiserId,
      authorizedAdvertiserId: currentAdvertiser?.id,
    });
    const oauthScopeVerified =
      resolvedOauthScope &&
      Boolean(returnAdvertiserId) &&
      !callbackSessionMismatch;

    const clearOauthReturnQuery = () => {
      params.delete("oauth");
      params.delete("provider");
      params.delete("error");
      params.delete("status");
      params.delete("advertiserId");
      params.delete("advertiser_id");
      params.delete("oauthAdvertiserId");
      const newSearch = params.toString();
      const newUrl = `${window.location.pathname}${
        newSearch ? `?${newSearch}` : ""
      }`;
      window.history.replaceState({}, "", newUrl);
    };

    if (!oauthScopeVerified) {
      setOauthBanner({
        type: "error",
        text:
          "OAUTH RETURN ADVERTISER COULD NOT BE VERIFIED. RESELECT THE AUTHORIZED ADVERTISER AND TRY AGAIN.",
      });
      clearOauthReturnQuery();
      return;
    }

    const p = String(provider || "").toUpperCase();

    const isSuccess =
      (oauth === "success" && provider) ||
      (provider && String(status || "").toLowerCase() === "connected");

    const isError =
      oauth === "error" ||
      String(status || "").toLowerCase() === "error" ||
      String(status || "").toLowerCase() === "failed";

    if (isSuccess && provider) {
      setOauthBanner({
        type: "success",
        text: `${providerPrettyName(provider)} connection completed successfully.`,
      });

      if (p && DEFAULT_INTEGRATION_STATE[p]) {
        setIntegrationStatus((prev) => ({
          ...prev,
          [p]: {
            ...(prev[p] || DEFAULT_INTEGRATION_STATE[p]),
            status: "connected",
            label: DEFAULT_INTEGRATION_STATE[p].label,
          },
        }));
      }

      setIntegrationRefreshKey((k) => k + 1);

      if (p === "META") {
        setMetaMessage(
          "META CONNECTED. LOAD META ACCOUNTS. CPHERE WILL AUTO-LINK ONE ELIGIBLE MANAGED ACCOUNT OR PROMPT YOU TO LINK THE CORRECT ACCOUNT."
        );
        setMetaAccountsLoadedFor(null);
        setMetaAutoConfiguredFor(null);
        setMetaLinkingState(META_LINKING_STATE_EMPTY);
      }
    } else if (isError) {
      setOauthBanner({
        type: "error",
        text:
          errorMsg ||
          "There was an error completing the connection. Please try again.",
      });

      setIntegrationRefreshKey((k) => k + 1);
    }

    clearOauthReturnQuery();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advertiserCatalogLoading, selectedAdvertiserId]);

  const getAuthorizePathForProvider = (provider) => {
    switch (provider) {
      case "META":
        return "/api/oauth/meta/authorize";
      case "TIKTOK":
        return "/api/oauth/tiktok/authorize";
      case "TIKTOK_LOGIN":
        return "/api/oauth/tiktok-login/authorize";
      case "X":
        return "/api/oauth/x/authorize";
      case "GOOGLE":
        return "/api/oauth/google/authorize";
      case "GOOGLE_ANALYTICS":
        return "/api/oauth/google-analytics/authorize";
      case "REDDIT":
        return "/api/oauth/reddit/authorize";
      case "AMAZON":
        return "/api/oauth/amazon/authorize";
      case "SHOPIFY":
        return "/api/oauth/shopify/authorize";
      case "LINKEDIN":
        return "/api/oauth/linkedin/authorize";
      default:
        return null;
    }
  };

  const startOAuthSafe = async ({
    provider,
    advertiserId,
    scopeEpoch,
    returnToOrigin,
    extraQueryParams = {},
  }) => {
    if (!apiBase || !advertiserId) return;

    const token = getAuthToken();
    const portal = "client";
    const path = getAuthorizePathForProvider(provider);
    if (!path) {
      throw new Error("UNKNOWN PROVIDER AUTHORIZE PATH.");
    }

    if (!token) {
      throw new Error("YOUR SESSION HAS EXPIRED. PLEASE LOG IN AGAIN.");
    }

    const prettyProvider = providerPrettyName(provider);

    const queryParams = new URLSearchParams({
      advertiserId: String(advertiserId),
      portal,
      returnTo: String(returnToOrigin || ""),
      ts: String(Date.now()),
    });
    Object.entries(extraQueryParams || {}).forEach(([key, value]) => {
      const text = String(value ?? "").trim();
      if (key && text) queryParams.set(key, text);
    });
    const baseUrl = `${apiBase}${path}?${queryParams.toString()}`;

    try {
      sessionStorage.setItem(SELECTED_ADV_ID_KEY, String(advertiserId || ""));
      sessionStorage.setItem(
        SELECTED_ADV_NAME_KEY,
        String(selectedAdvertiserName || "")
      );
    } catch (_e) {}

    // ✅ Require JSON-mode OAuth start so auth headers are preserved.
    // ✅ Do NOT fall back to direct browser navigation to protected /authorize routes.
    if (!providerSupportsJsonAuthorize(provider)) {
      throw new Error(
        `${prettyProvider.toUpperCase()} OAUTH START REQUIRES BACKEND JSON AUTHORIZE SUPPORT. ADD mode=json SUPPORT TO ${path}.`
      );
    }

    const jsonUrl = `${baseUrl}&mode=json`;

    let res;
    try {
      res = await fetch(jsonUrl, {
        method: "GET",
        headers: {
          ...authHeaders(),
          Accept: "application/json",
        },
        credentials: "include",
        cache: "no-store",
        redirect: "follow",
      });
    } catch (e) {
      const msg = String(e?.message || "");
      const looksAuth =
        msg.toLowerCase().includes("unauthorized") ||
        msg.toLowerCase().includes("no token") ||
        msg.toLowerCase().includes("forbidden");

      if (looksAuth) {
        throw new Error(msg || "UNAUTHORIZED. PLEASE LOG IN AGAIN.");
      }

      throw new Error(
        msg || `UNABLE TO START ${prettyProvider.toUpperCase()} OAUTH FLOW.`
      );
    }

    let payload = null;
    try {
      payload = await res.json();
    } catch (_e) {
      payload = null;
    }

    if (res.status === 401 || res.status === 403) {
      throw new Error(
        payload?.error || payload?.message || "UNAUTHORIZED. PLEASE LOG IN AGAIN."
      );
    }

    if (!res.ok) {
      throw new Error(
        payload?.error ||
          payload?.message ||
          `FAILED TO START ${prettyProvider.toUpperCase()} OAUTH.`
      );
    }

    const authorizeUrl =
      payload?.authorizeUrl || payload?.url || payload?.data?.authorizeUrl || "";

    if (!authorizeUrl) {
      console.warn("[Integrations] OAuth authorize response missing authorizeUrl", {
        provider,
        path,
        status: res.status,
      });
      throw new Error(
        `${prettyProvider.toUpperCase()} AUTHORIZE URL NOT RETURNED BY BACKEND. CONFIRM ${path} IS MOUNTED AND RETURNS { authorizeUrl }.`
      );
    }

    // An advertiser change invalidates this OAuth start. Never redirect a
    // newly selected scope into an authorization flow created for the prior
    // advertiser.
    if (scopeEpoch !== advertiserScopeEpochRef.current) return false;
    window.location.assign(authorizeUrl);
    return true;
  };

  const handleConnectClick = async (provider) => {
    const providerKey = String(provider || "").toUpperCase();
    if (!selectedAdvertiserId) {
      setIntegrationError("PLEASE SELECT AN ADVERTISER BEFORE CONNECTING.");
      return;
    }

    if (!apiBase) {
      setIntegrationError("BACKEND URL NOT CONFIGURED. PLEASE CONTACT YOUR ADMIN.");
      return;
    }

    const path = getAuthorizePathForProvider(provider);
    if (!path) {
      console.warn("[Integrations] Missing OAuth authorize path", { provider: providerKey });
      setIntegrationError("UNKNOWN PROVIDER INTEGRATION.");
      return;
    }

    const extraQueryParams = {};
    if (providerKey === "META") {
      // The advertiser-client Meta flow is intentionally limited to the one
      // permission under App Review. Broader operational scopes, when needed,
      // use a separate admin-controlled connection path.
      extraQueryParams.permissionProfile = "ads_read_review";
    }
    if (providerKey === "SHOPIFY") {
      const shopDomain = normalizeShopifyShopDomainForUi(
        shopifyShopDomainInput ||
          integrationStatus?.SHOPIFY?.shopDomain ||
          integrationStatus?.SHOPIFY?.externalAccountId ||
          ""
      );
      if (!isValidShopifyShopDomainForUi(shopDomain)) {
        setIntegrationError(
          "ENTER A VALID SHOPIFY MYSHOPIFY.COM STORE DOMAIN BEFORE CONNECTING SHOPIFY."
        );
        return;
      }
      setShopifyShopDomainInput(shopDomain);
      extraQueryParams.shop = shopDomain;
    }

    setIntegrationError("");
    setOauthLoadingProvider(provider);

    const requestAdvertiserId = selectedAdvertiserId;
    const scopeEpoch = advertiserScopeEpochRef.current;
    const requestIsCurrent = () =>
      scopeEpoch === advertiserScopeEpochRef.current;
    const returnToOrigin = getClientReturnToOrigin();

    try {
      await startOAuthSafe({
        provider,
        advertiserId: requestAdvertiserId,
        scopeEpoch,
        returnToOrigin,
        extraQueryParams,
      });
    } catch (e) {
      if (requestIsCurrent()) {
        setIntegrationError(e?.message || "UNABLE TO START OAUTH FLOW.");
      }
    } finally {
      if (requestIsCurrent()) setOauthLoadingProvider(null);
    }
  };

  const performDisconnect = async () => {
    const provider = pendingDisconnectProvider;
    if (!provider) return;

    closeDisconnectModal();

    if (!selectedAdvertiserId) {
      setIntegrationError("PLEASE SELECT AN ADVERTISER BEFORE DISCONNECTING.");
      return;
    }

    const requestAdvertiserId = selectedAdvertiserId;
    const scopeEpoch = advertiserScopeEpochRef.current;
    const requestIsCurrent = () =>
      scopeEpoch === advertiserScopeEpochRef.current;

    try {
      const token = getAuthToken();

      if (!apiBase) {
        setIntegrationError("BACKEND URL NOT CONFIGURED.");
        return;
      }

      const primary = `${apiBase}/api/integrations/${encodeURIComponent(
        requestAdvertiserId
      )}/${encodeURIComponent(provider)}/disconnect`;

      const fallback = `${apiBase}/api/oauth/integrations/${encodeURIComponent(
        requestAdvertiserId
      )}/${encodeURIComponent(provider)}/disconnect`;

      const out = await fetchFirstOk([primary, fallback], {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
        cache: "no-store",
      });

      if (!requestIsCurrent()) return;
      if (!out?.res) throw new Error(out?.error || "NO RESPONSE");
      if (!out.res.ok) {
        const msg =
          out?.json?.message ||
          out?.json?.error ||
          out?.text ||
          "FAILED TO DISCONNECT INTEGRATION.";
        throw new Error(msg);
      }

      setIntegrationStatus((prev) => ({
        ...prev,
        [provider]: {
          ...(prev[provider] || {}),
          status: "not_connected",
          connected: false,
          connectedAccount: null,
          connectedAccountName: null,
          externalAccountId: null,
          accountId: null,
          metaAdAccountId: null,
          metaAdAccountName: null,
          selectedAdAccountId: null,
          lastSyncedAt: null,
          expiresAt: null,
          label:
            DEFAULT_INTEGRATION_STATE[provider]?.label || providerPrettyName(provider),
        },
      }));

      setIntegrationError("");
      setIntegrationRefreshKey((k) => k + 1);

      if (String(provider).toUpperCase() === "META") {
        try {
          if (metaFetchAbortRef.current) metaFetchAbortRef.current.abort();
        } catch (_e) {}
        setMetaAccountsLoadedFor(null);
        setMetaAutoConfiguredFor(null);
        setMetaAdAccounts([]);
        setMetaSelectedAdAccountId("");
        setMetaLinkingState(META_LINKING_STATE_EMPTY);
        setMetaMessage("");
        setMetaTestWindow("");
        setMetaReadResult(null);
        setMetaError("");
      }
    } catch (err) {
      if (requestIsCurrent()) {
        setIntegrationError(
          err?.message || "UNABLE TO DISCONNECT. PLEASE TRY AGAIN."
        );
      }
    }
  };

  const handleDisconnectClick = (provider) => {
    if (!selectedAdvertiserId) {
      setIntegrationError("PLEASE SELECT AN ADVERTISER BEFORE DISCONNECTING.");
      return;
    }
    openDisconnectModal(provider);
  };

  const renderStatusBadge = (status, providerStatus = null) => {
    const normalized = String(status || "").toLowerCase();

    let label = "Not Connected";
    let statusClass = "not-connected";

    if (normalized === "connected") {
      // The backend's five-part truth says whether Cphere actually read the
      // provider. A credential or a stored flag alone is not a connection
      // Cphere has verified, so it is named as what it is.
      const verification = providerStatus?.truth?.verificationState;
      label = verification && verification.state !== "READ_ACCESS_VERIFIED" ? "Credential present" : "Connected";
      statusClass = "connected";
    } else if (normalized === "setup") {
      label = "Setup";
      statusClass = "expired";
    } else if (normalized === "expired") {
      label = "Expired";
      statusClass = "expired";
    } else if (normalized === "error") {
      label = "Error";
      statusClass = "error";
    } else if (normalized === "coming_soon" || normalized === "coming-soon") {
      label = "Coming Soon";
      statusClass = "coming-soon";
    }

    return (
      <span className={`integration-status-badge ${statusClass}`}>{label}</span>
    );
  };

  const getCanonicalAccountRecord = (providerStatus) => {
    if (providerStatus?.accountRecord && typeof providerStatus.accountRecord === "object") {
      return providerStatus.accountRecord;
    }
    return {};
  };

  const getIntegrationAccountText = (value) => {
    if (value === undefined || value === null) return "";
    if (typeof value === "object") {
      return String(
        value.connectedAccountLabel ||
          value.connectedAccountName ||
          value.externalAccountName ||
          value.metaAdAccountName ||
          value.adAccountName ||
          value.accountName ||
          value.businessName ||
          value.pageName ||
          value.name ||
          value.label ||
          ""
      ).trim();
    }
    return String(value).trim();
  };

  const isOpaqueMetaAccountId = (value) => {
    const text = String(value || "").trim();
    return /^act_\d+$/i.test(text) || /^\d{6,}$/.test(text);
  };

  const pickFirstAccountLabel = (values = [], options = {}) => {
    const { skipOpaqueMetaIds = false } = options;
    for (const value of values) {
      const text = getIntegrationAccountText(value);
      if (!text) continue;
      if (skipOpaqueMetaIds && isOpaqueMetaAccountId(text)) continue;
      return text;
    }
    return "";
  };

  const getSelectedAdvertiserDisplayLabel = () =>
    getIntegrationAccountText(
      selectedAdvertiserObject?.advertiser ||
        selectedAdvertiserObject?.name ||
        selectedAdvertiserName
    );

  const getFallbackConnectedAccountPresentation = () => {
    const display = getSelectedAdvertiserDisplayLabel();
    return display ? { label: "Connected Account", value: display } : null;
  };

  const getMetaConnectedAccountLabel = (providerStatus, accountRecord) => {
    const metaLabel = pickFirstAccountLabel(
      [
        providerStatus.connectedAccountLabel,
        providerStatus.connectedAccountName,
        providerStatus.metaConnectedAccountName,
        providerStatus.metaAdAccountName,
        providerStatus.adAccountName,
        providerStatus.accountName,
        providerStatus.externalAccountName,
        accountRecord.connectedAccountLabel,
        accountRecord.connectedAccountName,
        accountRecord.externalAccountName,
        providerStatus.connectedAccount,
      ],
      { skipOpaqueMetaIds: true }
    );

    return (
      metaLabel ||
      getSelectedAdvertiserDisplayLabel() ||
      maskMetaAdAccountId(
        pickFirstAccountLabel([
          providerStatus.metaAdAccountId,
          accountRecord.externalAccountId,
          providerStatus.externalAccountId,
          providerStatus.accountId,
          providerStatus.connectedAccount,
        ])
      )
    );
  };

  const formatIntegrationIdentityDisplay = (name, id) => {
    const normalizedName = String(name || "").trim();
    const normalizedId = String(id || "").trim();

    if (normalizedName && normalizedId) return `${normalizedName} (${normalizedId})`;
    if (normalizedName) return normalizedName;
    if (normalizedId) return normalizedId;
    return "";
  };

  const getAccountOfRecordDisplay = (providerStatus) => {
    const accountRecord = getCanonicalAccountRecord(providerStatus);
    const accountName =
      accountRecord.connectedAccountLabel ||
      providerStatus.connectedAccountLabel ||
      accountRecord.connectedAccountName ||
      accountRecord.externalAccountName ||
      providerStatus.externalAccountName ||
      providerStatus.connectedAccountName ||
      providerStatus.connectedAccount ||
      "";
    const accountId =
      accountRecord.externalAccountId ||
      providerStatus.externalAccountId ||
      providerStatus.propertyId ||
      providerStatus.profileId ||
      providerStatus.accountId ||
      "";

    if (accountName && accountId) return `${accountName} (${accountId})`;
    if (accountName) return accountName;
    if (accountId) return accountId;
    return "Not Saved";
  };

  const getIntegrationIdentityPresentation = (
    providerKey,
    providerStatus,
    options = {}
  ) => {
    const { allowSelectedAdvertiserFallback = false } = options;
    const getFallbackPresentation = () =>
      allowSelectedAdvertiserFallback
        ? getFallbackConnectedAccountPresentation()
        : null;
    const normalizedProvider = String(
      providerKey || providerStatus?.provider || ""
    ).trim().toUpperCase();
    const accountRecord = getCanonicalAccountRecord(providerStatus);

    if (normalizedProvider === "META") {
      const display = getMetaConnectedAccountLabel(providerStatus, accountRecord);
      return display ? { label: "Connected Account", value: display } : null;
    }

    const genericDisplay = getAccountOfRecordDisplay(providerStatus);

    if (!genericDisplay || genericDisplay === "Not Saved") {
      return getFallbackPresentation();
    }

    if (normalizedProvider === "TIKTOK") {
      const advertiserName =
        accountRecord.externalAccountName ||
        providerStatus.externalAccountName ||
        providerStatus.connectedAccountName ||
        providerStatus.connectedAccount ||
        "";
      const advertiserId =
        accountRecord.externalAccountId || providerStatus.externalAccountId || "";
      const display = formatIntegrationIdentityDisplay(advertiserName, advertiserId);
      return display
        ? { label: "Connected Advertiser", value: display }
        : { label: "Connected Advertiser", value: genericDisplay };
    }

    if (normalizedProvider === "TIKTOK_LOGIN") {
      const brandName =
        providerStatus.connectedAccountName ||
        providerStatus.connectedAccount ||
        accountRecord.connectedAccountName ||
        accountRecord.externalAccountName ||
        "";
      const profileId =
        providerStatus.externalAccountId || accountRecord.externalAccountId || "";
      const display = formatIntegrationIdentityDisplay(brandName, profileId);
      return display
        ? {
            label: profileId ? "Connected Profile" : "Authorized Brand",
            value: display,
          }
        : getFallbackPresentation();
    }

    return { label: "Connected Account", value: genericDisplay };
  };

  const renderCanonicalAccountHighlights = (
    providerKey,
    providerStatus,
    options = {}
  ) => {
    const identityPresentation = getIntegrationIdentityPresentation(
      providerKey,
      providerStatus,
      options
    );

    // Precedence: per-provider frontend override (Meta memo) → backend
    // normalized label → humanized raw source. Raw "missing" never renders;
    // the row is hidden entirely when no meaningful mapping state exists.
    const rawMappingSource =
      providerStatus?.accountRecord?.mappingSource ||
      providerStatus?.mappingSource ||
      null;
    const mappingSourceDisplay =
      options.mappingSourceDisplay ||
      providerStatus?.mappingSourceLabel ||
      (rawMappingSource && String(rawMappingSource).toLowerCase() !== "missing"
        ? String(rawMappingSource)
            .replace(/_/g, " ")
            .replace(/\b\w/g, (match) => match.toUpperCase())
        : null);

    if (!identityPresentation?.value && !mappingSourceDisplay) return null;

    return (
      <div className="integration-card-highlights">
        {identityPresentation?.value ? (
          <div className="integration-card-highlight">
            <span className="integration-card-highlight-label">
              {identityPresentation.label}
            </span>
            <span className="integration-card-highlight-value">
              {identityPresentation.value}
            </span>
          </div>
        ) : null}
        {mappingSourceDisplay ? (
          <div className="integration-card-highlight">
            <span className="integration-card-highlight-label">Mapping Source</span>
            <span className="integration-card-highlight-value">
              {mappingSourceDisplay}
            </span>
          </div>
        ) : null}
      </div>
    );
  };

  const isClientCardConnected = (providerKey, providerStatus = {}) =>
    getPrimaryConnectionStatus(providerKey, providerStatus) === "connected";

  const getPrimaryConnectionStatus = (providerKey, providerStatus = {}) => {
    const normalizedProvider = String(providerKey || "").trim().toUpperCase();
    const normalizedStatus = String(providerStatus?.status || "").toLowerCase();

    if (normalizedStatus === "expired") return "expired";
    if (normalizedStatus === "error") return "error";

    const isConnected =
      normalizedProvider === "AMAZON"
        ? Boolean(providerStatus?.connected)
        : Boolean(providerStatus?.connected || normalizedStatus === "connected");

    return isConnected ? "connected" : "not_connected";
  };

  const connectButtonLabel = (providerKey) => {
    if (oauthLoadingProvider === providerKey) {
      return "CONNECTING...";
    }

    const s = getPrimaryConnectionStatus(
      providerKey,
      integrationStatus?.[providerKey] || {}
    );

    if (s === "connected") return "DISCONNECT";
    if (s === "expired") return "CONNECT";
    return "CONNECT";
  };

  const connectButtonMode = (providerKey) => {
    const s = getPrimaryConnectionStatus(
      providerKey,
      integrationStatus?.[providerKey] || {}
    );
    return s === "connected" ? "disconnect" : "connect";
  };

  const ProviderCard = ({
    providerKey,
    logoSrc,
    logoAlt,
    description,
  }) => {
    const providerStatus = integrationStatus?.[providerKey] || {};
    const lifecycle =
      providerStatus.lifecycle ||
      providerStatus.integrationNotes?.lifecycle ||
      {};
    const primaryConnectionStatus = getPrimaryConnectionStatus(
      providerKey,
      providerStatus
    );
    const providerConnected = isClientCardConnected(providerKey, providerStatus);
    const isDisconnectedMode = connectButtonMode(providerKey) === "disconnect";
    const isThisProviderLoading = oauthLoadingProvider === providerKey;
    const isTikTok = String(providerKey || "").toUpperCase() === "TIKTOK";
    const isShopify = String(providerKey || "").toUpperCase() === "SHOPIFY";
    const isMeta = String(providerKey || "").toUpperCase() === "META";
    const isAmazon = String(providerKey || "").toUpperCase() === "AMAZON";
    // Amazon annual-authorization lifecycle (backend-derived, secret-free).
    // Advertisers see a neutral renewal prompt — never internal lifecycle
    // mechanics, never token detail.
    const amazonTokenLifecycle = isAmazon ? providerStatus.tokenLifecycle || null : null;
    // Amazon Sponsored Ads reporting, in the client's words: the state, the
    // last import and the day it covers. No reason and no held-row counts —
    // those name operator work.
    const amazonReporting = isAmazon
      ? readAmazonReporting(providerStatus.reporting, { audience: "client" })
      : null;
    const amazonMcp = isAmazon
      ? providerStatus.amazonMcp || null
      : null;
    const amazonMcpEnabled = isAmazonMcpAdvertiserEnabled(amazonMcp);
    const amazonMcpState = String(amazonMcp?.state || "DISABLED_BY_POLICY").toUpperCase();
    const amazonMcpSummary = amazonMcpEnabled
      ? amazonMcpState === "READ_ONLY_READY"
        ? "CONNECTED · READ-ONLY READY"
        : amazonMcpState === "SCHEMA_CHANGED"
        ? "PAUSED · CAPABILITY REVIEW REQUIRED"
        : amazonMcpState === "REAUTH_REQUIRED"
        ? "RECONNECT REQUIRED"
        : amazonMcpState === "CONFIGURED_NOT_VERIFIED"
        ? "CONNECTED · VERIFICATION PENDING"
        : amazonMcpState === "DEGRADED" || amazonMcpState === "ERROR"
        ? "TEMPORARILY DEGRADED"
        : amazonMcpState === "NOT_CONFIGURED"
        ? "PROFILE OR MARKETPLACE SETUP REQUIRED"
        : "NOT ENABLED"
      : null;
    const amazonLifecycleStatusKey = String(amazonTokenLifecycle?.status || "").toUpperCase();
    const amazonReauthRequired = ["REAUTH_REQUIRED", "REVOKED"].includes(amazonLifecycleStatusKey);
    const amazonExpiringSoon = amazonLifecycleStatusKey === "EXPIRING";
    const amazonExpiryDaysRemaining = Number.isFinite(
      amazonTokenLifecycle?.daysUntilRefreshTokenExpires
    )
      ? Math.max(amazonTokenLifecycle.daysUntilRefreshTokenExpires, 0)
      : null;
    const metaSelectedValue = ensureActPrefix(
      metaSelectedAdAccountId || metaStoredAct || ""
    );
    const metaAccountOptions = (metaAdAccounts || [])
      .map((account) => {
        const id = ensureActPrefix(
          account?.id || account?.account_id || account?.adAccountId || ""
        );
        if (!id) return null;
        const name = String(account?.name || account?.account_name || "").trim();
        return {
          id,
          label: formatMaskedMetaAccount(name, id),
        };
      })
      .filter(Boolean);
    const metaPerformanceEvidence = metaReadResult?.performanceEvidence || null;
    const metaCampaignPerformanceEvidence = Array.isArray(
      metaReadResult?.campaignPerformanceEvidence
    )
      ? metaReadResult.campaignPerformanceEvidence
      : [];
    const metaReviewerEvidenceSummary =
      metaReadResult?.reviewerEvidenceSummary || null;
    const metaCampaignEvidenceRows = metaCampaignPerformanceEvidence.length
      ? metaCampaignPerformanceEvidence
      : metaPerformanceEvidence
      ? [
          {
            ...metaPerformanceEvidence,
            evidenceState:
              metaPerformanceEvidence.reviewerEvidenceState === "REVIEWER_READY"
                ? "PERFORMANCE_RETURNED"
                : "NO_PERFORMANCE_ROWS",
            isDesignatedReviewCampaign: true,
          },
        ]
      : [];
    const metaEvidenceState = String(
      metaReviewerEvidenceSummary?.reviewerEvidenceState ||
        metaPerformanceEvidence?.reviewerEvidenceState ||
        "BLOCKED_BY_DATA_OR_ACCESS"
    );
    const metaCampaignReadVerified =
      metaEvidenceState === "REVIEWER_READY" ||
      metaPerformanceEvidence?.targetCampaignVerified === true;
    const metaReviewerVerificationCopy =
      metaEvidenceState === "REVIEWER_READY"
        ? `Verification successful: ads_read retrieved ${Number(
            metaReviewerEvidenceSummary?.campaignsReturned ||
              metaReadResult?.campaignsCount ||
              0
          )} campaigns and fresh performance for ${Number(
            metaReviewerEvidenceSummary?.campaignsWithPerformance || 0
          )}`
        : metaCampaignReadVerified
        ? "Verification successful: ads_read retrieved the campaign; Meta returned no performance rows"
        : "Verification incomplete: Meta did not return the expected campaign";
    const metaReadTestDisabled =
      !selectedAdvertiserId ||
      !metaIsConnected ||
      !metaHasSavedAccount ||
      metaHasPendingSelectionChange ||
      metaLoading ||
      isThisProviderLoading;
    const metaSaveDisabled =
      !selectedAdvertiserId ||
      !metaIsConnected ||
      metaLoading ||
      isThisProviderLoading ||
      !metaSelectedValue ||
      (metaHasSavedAccount && !metaHasPendingSelectionChange);
    const metaManualSelectionNeeded =
      isMeta &&
      metaIsConnected &&
      !metaHasSavedAccount &&
      Boolean(
        metaLinkingState.manualSelectionRequired || metaAccountOptions.length > 1
      );
    const metaShowAccountSelector = metaManualSelectionNeeded;
    const metaShowLinkButton = metaManualSelectionNeeded;
    const tiktokMapped = Boolean(providerStatus.mapped || lifecycle.mapped);
    const tiktokNeedsManualMapping =
      isTikTok &&
      providerConnected &&
      !Boolean(providerStatus.externalAccountId || providerStatus.mapped || lifecycle.mapped);
    const tiktokFallbackReason =
      String(providerStatus.reason || "").trim() ||
      "TikTok connected successfully, but TikTok did not provide a selected advertiser ID for this advertiser. Save the advertiser ID once to finish the account-of-record setup.";
    return (
      <div className="integration-card">
        <div className="integration-card-body">
          <div className="integration-card-header">
            <div className="integration-card-icon-wrapper">
              <img src={logoSrc} alt={logoAlt} className="integration-card-icon" />
            </div>
            <div className="integration-card-title-group">
              <h2 className="integration-card-title">
                {integrationStatus?.[providerKey]?.label ||
                  providerPrettyName(providerKey)}
              </h2>
              {renderStatusBadge(primaryConnectionStatus, integrationStatus?.[providerKey])}
            </div>
          </div>

          <p className="integration-card-description">
            {getIntegrationCardDescription(providerKey, description)}
          </p>

          {isShopify ? (
            <div className="integration-card-highlights integration-card-highlights--editable">
              <div className="integration-card-highlight integration-card-highlight--editable integration-card-highlight--shop-domain">
                <label
                  className="integration-card-highlight-label"
                  htmlFor={`${providerKey}-shop-domain`}
                >
                  Shopify Store Domain
                </label>
                <input
                  id={`${providerKey}-shop-domain`}
                  className="integration-card-highlight-input integration-card-highlight-input--governed integration-card-highlight-input--shop-domain"
                  type="text"
                  value={shopifyShopDomainInput}
                  onChange={(event) => setShopifyShopDomainInput(event.target.value)}
                  onBlur={() =>
                    setShopifyShopDomainInput((value) =>
                      normalizeShopifyShopDomainForUi(value)
                    )
                  }
                  placeholder="your-store.myshopify.com"
                  disabled={!selectedAdvertiserId || isThisProviderLoading}
                />
              </div>
            </div>
          ) : null}

          {providerConnected
            ? renderCanonicalAccountHighlights(providerKey, providerStatus, {
                allowSelectedAdvertiserFallback: true,
                mappingSourceDisplay:
                  String(providerKey || "").toUpperCase() === "META"
                    ? metaMappingStatusDisplay
                    : null,
              })
            : null}

          {/* Sponsored Ads reporting: the state in words, the last import
              (never a connection check) and the day it covers. */}
          {isAmazon && providerConnected && amazonReporting ? (
            <div
              className="integration-card-highlights"
              data-amazon-reporting-status={amazonReporting.status || "unknown"}
            >
              <div className="integration-card-highlight">
                <span className="integration-card-highlight-label">Sponsored Ads Reporting</span>
                <span className="integration-card-highlight-value">{amazonReporting.label}</span>
              </div>
              {amazonReporting.lastImportAt ? (
                <div className="integration-card-highlight">
                  <span className="integration-card-highlight-label">Last Import</span>
                  <span className="integration-card-highlight-value">
                    {formatAmazonReportingTime(amazonReporting.lastImportAt)}
                  </span>
                </div>
              ) : null}
              {amazonReporting.dataThrough ? (
                <div className="integration-card-highlight">
                  <span className="integration-card-highlight-label">Data Through</span>
                  <span className="integration-card-highlight-value">
                    {formatAmazonDataThrough(amazonReporting.dataThrough)}
                  </span>
                </div>
              ) : null}
            </div>
          ) : null}

          {/* The AI assistant's on-demand Amazon connection (MCP). It is not
              the reporting import above, and is labelled so it cannot be
              read as one. */}
          {isAmazon && amazonMcpEnabled && amazonMcpSummary ? (
            <div className="integration-card-highlights">
              <div className="integration-card-highlight">
                <span className="integration-card-highlight-label">AI Assistant Connection (MCP)</span>
                <span className="integration-card-highlight-value">{amazonMcpSummary}</span>
              </div>
              {amazonMcp?.lastSuccessfulHandshake ? (
                <div className="integration-card-highlight">
                  <span className="integration-card-highlight-label">AI Assistant Last Verified</span>
                  <span className="integration-card-highlight-value">
                    {new Date(amazonMcp.lastSuccessfulHandshake).toLocaleString()}
                  </span>
                </div>
              ) : null}
            </div>
          ) : null}

          {isAmazon && (amazonReauthRequired || amazonExpiringSoon) ? (
            <div className="integration-card-actions integration-card-actions--stacked">
              <p className="integration-card-summary-copy">
                {amazonReauthRequired
                  ? "Amazon requires your authorization to be renewed each year for advertising access. Reconnecting takes about a minute, keeps your saved Amazon Ads profile, and restores campaign and audience features. Your historical reporting is unaffected."
                  : amazonExpiryDaysRemaining != null
                  ? `Your Amazon Ads authorization expires in ${amazonExpiryDaysRemaining} day${amazonExpiryDaysRemaining === 1 ? "" : "s"}. Renew it now to avoid any interruption — your saved profile and reporting are kept.`
                  : "Your Amazon Ads authorization is expiring soon. Renew it now to avoid any interruption — your saved profile and reporting are kept."}
              </p>
              <button
                type="button"
                className="integration-button integration-button--secondary"
                onClick={() => handleConnectClick("AMAZON")}
                disabled={!selectedAdvertiserId || isThisProviderLoading}
              >
                <span className="integration-button-label">
                  {isThisProviderLoading
                    ? "OPENING AMAZON LOGIN..."
                    : "RECONNECT AMAZON ADS"}
                </span>
              </button>
            </div>
          ) : null}

          {isMeta ? (
            <div className="integration-meta-section integration-meta-section--meta">
              {metaIsConnected ? (
                <>
                  <div className="integration-card-highlights integration-card-highlights--editable">
                    <div className="integration-card-highlight">
                      <span className="integration-card-highlight-label">
                        Linked Meta Ad Account
                      </span>
                      <span className="integration-card-highlight-value">
                        {metaLinkedAccountDisplay}
                      </span>
                    </div>

                    {metaShowAccountSelector ? (
                      <div className="integration-card-highlight integration-card-highlight--editable">
                        <p className="integration-card-summary-copy">
                          Choose the Meta ad account Cphere should use for this advertiser.
                        </p>
                        <label
                          className="integration-card-highlight-label"
                          htmlFor={`${providerKey}-meta-ad-account`}
                        >
                          Meta Ad Account For ads_read
                        </label>
                        <select
                          id={`${providerKey}-meta-ad-account`}
                          className="integration-card-highlight-input"
                          value={metaSelectedValue}
                          onChange={(event) =>
                            setMetaSelectedAdAccountId(
                              ensureActPrefix(event.target.value)
                            )
                          }
                          disabled={
                            !selectedAdvertiserId ||
                            metaLoading ||
                            isThisProviderLoading
                          }
                        >
                          <option value="">
                            {isFetchingMetaAccounts
                              ? "Loading Meta accounts..."
                              : "Choose Meta ad account"}
                          </option>
                          {metaSelectedValue && !metaSelectedExistsInList ? (
                            <option value={metaSelectedValue}>
                              {maskMetaAdAccountId(metaSelectedValue)}
                            </option>
                          ) : null}
                          {metaAccountOptions.map((account) => (
                            <option key={account.id} value={account.id}>
                              {account.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    ) : null}
                  </div>

                  <div className="integration-card-actions integration-card-actions--stacked">
                    <p className="integration-card-summary-copy">
                      Meta login grants Cphere ads_read for read-only campaign and insights
                      reporting. Use this action to demonstrate the Meta login and permission
                      flow without disconnecting first.
                    </p>
                    <button
                      type="button"
                      className="integration-button integration-button--secondary"
                      onClick={() => handleConnectClick("META")}
                      disabled={!selectedAdvertiserId || metaLoading || isThisProviderLoading}
                    >
                      <span className="integration-button-label">
                        {isThisProviderLoading
                          ? "OPENING META LOGIN..."
                          : "OPEN META LOGIN & GRANT ADS_READ"}
                      </span>
                    </button>
                    <button
                      type="button"
                      className="integration-button integration-button--secondary"
                      onClick={() => fetchMetaAdAccounts({ force: true })}
                      disabled={
                        !selectedAdvertiserId ||
                        !metaIsConnected ||
                        metaLoading ||
                        isThisProviderLoading
                      }
                    >
                      <span className="integration-button-label">
                        {isFetchingMetaAccounts
                          ? "LOADING META ACCOUNTS..."
                          : "LOAD META ACCOUNTS"}
                      </span>
                    </button>
                    {metaShowLinkButton ? (
                      <button
                        type="button"
                        className="integration-button integration-button--secondary"
                        onClick={() => saveSelectedMetaAdAccount({ silent: false })}
                        disabled={metaSaveDisabled}
                      >
                        <span className="integration-button-label">
                          {metaLoading ? "LINKING META..." : "LINK META AD ACCOUNT"}
                        </span>
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="integration-button integration-button--secondary"
                      onClick={runMetaReadTest}
                      disabled={metaReadTestDisabled}
                    >
                      <span className="integration-button-label">
                        {metaLoading ? "RUNNING ADS_READ..." : "RUN ADS_READ TEST"}
                      </span>
                    </button>
                  </div>
                </>
              ) : null}

              {metaMessage ? (
                <div className="integrations-loading-message">{metaMessage}</div>
              ) : null}
              {metaMessage && metaTestWindow ? (
                <div className="integrations-loading-message integration-meta-test-window">
                  {metaTestWindow}
                </div>
              ) : null}
              {metaReadResult ? (
                <section
                  className="integration-meta-live-read"
                  aria-label="Live Meta ads_read result"
                >
                  <div className="integration-meta-live-read-title">
                    Live read-only Meta data displayed in Cphere
                  </div>
                  <div
                    className={`integration-meta-evidence-state ${
                      metaCampaignReadVerified
                        ? "integration-meta-evidence-state--ready"
                        : "integration-meta-evidence-state--blocked"
                    }`}
                  >
                    {metaReviewerVerificationCopy}
                  </div>
                  <p className="integration-meta-live-read-copy">
                    Retrieved from {metaReadResult.linkedAdAccount || "the linked Meta ad account"}
                    {" "}using ads_read. Cphere uses this permission for reporting and AI
                    analysis only; it does not create, edit, activate, or delete campaigns.
                  </p>
                  <div className="integration-meta-campaign-evidence-list">
                    {metaCampaignEvidenceRows.map((campaignEvidence, index) => {
                      const dateStart = formatMetaTestWindowDate(
                        campaignEvidence?.queriedDateStart
                      );
                      const dateStop = formatMetaTestWindowDate(
                        campaignEvidence?.queriedDateStop
                      );
                      const dateRange =
                        dateStart && dateStop
                          ? `${dateStart} → ${dateStop}`
                          : campaignEvidence?.reportingWindowSource ===
                            "meta_account_maximum"
                          ? "Maximum range queried; no row returned by Meta"
                          : "Not returned by Meta";
                      const hasPerformance =
                        campaignEvidence?.evidenceState === "PERFORMANCE_RETURNED";

                      return (
                        <article
                          className={`integration-meta-campaign-evidence ${
                            hasPerformance
                              ? "integration-meta-campaign-evidence--performance"
                              : "integration-meta-campaign-evidence--empty"
                          }`}
                          key={`${campaignEvidence?.campaignName || "meta-campaign"}-${index}`}
                        >
                          <div className="integration-meta-campaign-evidence-header">
                            <h3>{campaignEvidence?.campaignName || "Unnamed Meta campaign"}</h3>
                            <span>{campaignEvidence?.campaignStatus || "UNKNOWN"}</span>
                          </div>
                          {campaignEvidence?.isDesignatedReviewCampaign ? (
                            <div className="integration-meta-review-campaign-label">
                              Designated Meta App Review campaign
                            </div>
                          ) : null}
                          <div className="integration-meta-campaign-evidence-result">
                            {hasPerformance
                              ? "Fresh Meta campaign performance returned"
                              : "Meta returned no performance row for this campaign"}
                          </div>
                          <dl className="integration-meta-live-read-summary integration-meta-live-read-summary--evidence">
                            <div>
                              <dt>Campaign name</dt>
                              <dd>{campaignEvidence?.campaignName || "Not returned by Meta"}</dd>
                            </div>
                            <div>
                              <dt>Campaign status</dt>
                              <dd>{campaignEvidence?.campaignStatus || "Not returned by Meta"}</dd>
                            </div>
                            <div>
                              <dt>Meta campaign identity</dt>
                              <dd>
                                {campaignEvidence?.exactMetaCampaignIdentityMatched
                                  ? "Insights matched by Meta campaign ID"
                                  : "Campaign readable; no matching Insights row"}
                              </dd>
                            </div>
                            <div>
                              <dt>Exact queried reporting dates</dt>
                              <dd>{dateRange}</dd>
                            </div>
                            <div>
                              <dt>Spend</dt>
                              <dd>
                                {formatMetaEvidenceMetric(campaignEvidence?.spend, {
                                  currency: true,
                                })}
                              </dd>
                            </div>
                            <div>
                              <dt>Impressions</dt>
                              <dd>{formatMetaEvidenceMetric(campaignEvidence?.impressions)}</dd>
                            </div>
                            <div>
                              <dt>Reach (when available)</dt>
                              <dd>{formatMetaEvidenceMetric(campaignEvidence?.reach)}</dd>
                            </div>
                            <div>
                              <dt>Clicks</dt>
                              <dd>{formatMetaEvidenceMetric(campaignEvidence?.clicks)}</dd>
                            </div>
                            <div>
                              <dt>CPM</dt>
                              <dd>
                                {formatMetaEvidenceMetric(campaignEvidence?.cpm, {
                                  currency: true,
                                })}
                              </dd>
                            </div>
                            <div>
                              <dt>CTR</dt>
                              <dd>
                                {formatMetaEvidenceMetric(campaignEvidence?.ctr, {
                                  percent: true,
                                })}
                              </dd>
                            </div>
                            <div>
                              <dt>Retrieval timestamp</dt>
                              <dd>{formatMetaRetrievalTimestamp(campaignEvidence?.retrievedAt)}</dd>
                            </div>
                            <div>
                              <dt>Source</dt>
                              <dd>
                                {campaignEvidence?.source ||
                                  "Retrieved from Meta Marketing API"}
                              </dd>
                            </div>
                          </dl>
                          <p className="integration-meta-live-read-copy integration-meta-live-read-copy--evidence">
                            {campaignEvidence?.explanation ||
                              "No cached, imported, seeded, manually entered, partner, or separately stored reporting data is displayed here."}
                          </p>
                        </article>
                      );
                    })}
                  </div>
                </section>
              ) : null}
              {metaError ? (
                <div className="integrations-error-message">{metaError}</div>
              ) : null}
            </div>
          ) : null}

          {tiktokNeedsManualMapping ? (
            <div className="integration-meta-section integration-meta-section--tiktok">
              <p className="integration-card-fallback-note">{tiktokFallbackReason}</p>
              <div className="integration-card-highlights integration-card-highlights--editable">
                <div className="integration-card-highlight integration-card-highlight--editable">
                  <label
                    className="integration-card-highlight-label"
                    htmlFor={`${providerKey}-tiktok-advertiser-id`}
                  >
                    TikTok Advertiser ID
                  </label>
                  <input
                    id={`${providerKey}-tiktok-advertiser-id`}
                    className="integration-card-highlight-input integration-card-highlight-input--governed"
                    type="text"
                    value={tiktokMappedAdvertiserId}
                    onChange={(event) => setTiktokMappedAdvertiserId(event.target.value)}
                    placeholder="Enter advertiser/account id"
                    disabled={!selectedAdvertiserId || isThisProviderLoading || tiktokSavingMapping}
                  />
                </div>
              </div>
              {tiktokMessage ? (
                <div className="integrations-loading-message">{tiktokMessage}</div>
              ) : null}
              {tiktokError ? (
                <div className="integrations-error-message">{tiktokError}</div>
              ) : null}
            </div>
          ) : null}

        </div>

        <div className="integration-card-footer">
          <div
            className={`integration-card-actions ${
              tiktokNeedsManualMapping
                ? "integration-card-actions--stacked"
                : "integration-card-actions--single"
            }`}
          >
            <button
              className={`integration-button ${
                isDisconnectedMode
                  ? "integration-button--disconnect"
                  : "integration-button--connect"
              }`}
              onClick={() =>
                isDisconnectedMode
                  ? handleDisconnectClick(providerKey)
                  : handleConnectClick(providerKey)
              }
              disabled={!selectedAdvertiserId || isThisProviderLoading}
            >
              <span className="integration-button-label">
                {connectButtonLabel(providerKey)}
              </span>
            </button>
            {tiktokNeedsManualMapping ? (
              <button
                className="integration-button integration-button--secondary"
                onClick={saveSelectedTikTokAdvertiser}
                disabled={
                  !selectedAdvertiserId ||
                  isThisProviderLoading ||
                  tiktokSavingMapping ||
                  !String(tiktokMappedAdvertiserId || "").trim()
                }
              >
                <span className="integration-button-label">
                  {tiktokSavingMapping
                    ? "SAVING TIKTOK..."
                    : tiktokMapped
                    ? "UPDATE ACCOUNT"
                    : "SAVE ACCOUNT"}
                </span>
              </button>
            ) : null}
          </div>
        </div>
      </div>
    );
  };

  const renderIntegrationCardIcon = (logoSrc, logoAlt) => {
    const logos = Array.isArray(logoSrc) ? logoSrc.filter(Boolean) : [];
    if (logos.length > 1) {
      return (
        <div className="integration-card-icon-wrapper integration-card-icon-wrapper--duo">
          {logos.slice(0, 2).map((logo, index) => {
            const src = typeof logo === "string" ? logo : logo.src;
            const alt = typeof logo === "string" ? `${logoAlt || "Integration"} ${index + 1}` : logo.alt;
            return (
              <img
                key={`${alt || "integration"}-${index}`}
                src={src}
                alt={alt || logoAlt || "Integration"}
                className="integration-card-icon integration-card-icon-duo"
              />
            );
          })}
        </div>
      );
    }

    return (
      <div className="integration-card-icon-wrapper">
        <img
          src={logos.length ? logos[0]?.src || logos[0] : logoSrc}
          alt={logos.length ? logos[0]?.alt || logoAlt : logoAlt}
          className="integration-card-icon"
        />
      </div>
    );
  };

  const DerivedCard = ({ title, providerKey, logoSrc, logoAlt, isConnected, description }) => {
    return (
      <div className="integration-card">
        <div className="integration-card-body">
          <div className="integration-card-header">
            {renderIntegrationCardIcon(logoSrc, logoAlt)}
            <div className="integration-card-title-group">
              <h2 className="integration-card-title">{title}</h2>
              {renderStatusBadge(isConnected ? "connected" : "not_connected")}
            </div>
          </div>

          <p className="integration-card-description">
            {getIntegrationCardDescription(providerKey || title, description)}
          </p>

          {isConnected
            ? renderCanonicalAccountHighlights(providerKey || title, {}, {
                allowSelectedAdvertiserFallback: true,
              })
            : null}
        </div>
      </div>
    );
  };

  const StackAdaptCard = () => {
    const stackAdaptStatus = {
      ...DEFAULT_INTEGRATION_STATE.STACKADAPT,
      ...(integrationStatus?.STACKADAPT || {}),
    };
    const lifecycle =
      stackAdaptStatus.lifecycle ||
      stackAdaptStatus.integrationNotes?.lifecycle ||
      {};
    const connected = Boolean(stackAdaptStatus.connected || lifecycle.verified || stackAdaptConnected);

    return (
      <div className="integration-card">
        <div className="integration-card-body">
          <div className="integration-card-header">
            <div className="integration-card-icon-wrapper">
              <img
                src={cphereLogo}
                alt="Cphere"
                className="integration-card-icon integration-card-icon-cphere-brand"
              />
            </div>
            <div className="integration-card-title-group">
              <h2 className="integration-card-title">
                {stackAdaptStatus.label || "CPHERE"}
              </h2>
              {renderStatusBadge(connected ? "connected" : "not_connected")}
            </div>
          </div>

          <p className="integration-card-description">
            {getIntegrationCardDescription(
              "STACKADAPT",
              "Programmatic multi-channel analytics across premium media."
            )}
          </p>

          {connected
            ? renderCanonicalAccountHighlights("STACKADAPT", {}, {
                allowSelectedAdvertiserFallback: true,
                // StackAdapt has no per-advertiser mapping: the connection is
                // Cphere's shared provider/contract-level programmatic
                // integration, so that is the mapping source by design.
                mappingSourceDisplay:
                  stackAdaptStatus.mappingSourceLabel ||
                  (stackAdaptStatus.mappingState === "shared_provider_contract" ||
                  stackAdaptStatus.mappingNotRequired ||
                  stackAdaptStatus.envFallbackActive ||
                  String(stackAdaptStatus.providerModel || "").toLowerCase() ===
                    "programmatic_multi_channel"
                    ? "Shared Provider Contract"
                    : null),
              })
            : null}
        </div>
      </div>
    );
  };

  return (
    <div className="integrations-dashboard core-dashboard omni-analytics-page-container">
      <div className="integrations-content integrations-page-content core-dashboard-content omni-analytics-page-content">
        <CphereHeaderLogo
          alt="Powered by Logo"
          onClick={() => navigate("/dashboard")}
          style={{ cursor: "pointer" }}
        />

        <div className="cphere-governed-hero-selector-stack platform-connect-governed-advertiser-shell">
          <div className="omni-analytics-hero-container">
            <div className="omni-analytics-hero-pill">
              <span className="omni-analytics-hero-pill-label cphere-hero-pill-wordmark">Platform</span>
              <span className="omni-analytics-hero-pill-tag cphere-hero-pill-tag">Connect</span>
            </div>
            <div className="omni-analytics-hero-shimmer-bar" />
          </div>

          <div className="cphere-governed-advertiser-selector-stage">
            <AdvertiserIdentityTrigger
              advertiser={currentAdvertiser}
              advertiserName={currentAdvertiser?.name || ""}
              apiBase={apiBase}
              disabled={selectorDisabled}
              loading={advertiserCatalogLoading}
              empty={selectorEmpty}
              modalOpen={advertiserSelectorOpen}
              onClick={openAdvertiserSelector}
            />
          </div>
        </div>

        {advertiserSelectorOpen && visibleAdvertisers.length ? (
          <GovernedAdvertiserSelectorModal
            advertisers={visibleAdvertisers}
            selectedAdvertiserId={selectedAdvertiserId}
            selectedAdvertiserName={currentAdvertiser?.name || ""}
            apiBase={apiBase}
            onSelect={handleAdvertiserSelect}
            onClose={closeAdvertiserSelector}
          />
        ) : null}

        {oauthBanner && oauthBanner.type === "success" && (
          <div className="integrations-loading-message">{oauthBanner.text}</div>
        )}
        {oauthBanner && oauthBanner.type === "error" && (
          <div className="integrations-error-message">{oauthBanner.text}</div>
        )}

        {advertiserCatalogError && (
          <div className="integrations-error-message" role="alert">
            {advertiserCatalogErrorMessage || "UNABLE TO LOAD AUTHORIZED ADVERTISERS."}
          </div>
        )}
        {integrationError && (
          <div className="integrations-error-message">{integrationError}</div>
        )}

        {currentAdvertiser ? (
          <div className="integration-cards-grid">
          <ProviderCard
            providerKey="META"
            logoSrc={metaLogo}
            logoAlt="Meta"
            description="Connect a Meta ad account so Cphere can read campaigns and insights for Sherloc and Optimus reporting and AI analysis."
          />

          <ProviderCard
            providerKey="TIKTOK"
            logoSrc={tiktokLogo}
            logoAlt="TikTok"
            description="Connect a TikTok ad account so Cphere can read campaigns and insights for reporting and AI analysis."
          />

          <ProviderCard
            providerKey="TIKTOK_LOGIN"
            logoSrc={tiktokLogo}
            logoAlt="TikTok Login"
            description="Optional: connect TikTok Login Kit for identity-level permissions where available."
          />

          <ProviderCard
            providerKey="X"
            logoSrc={xLogo}
            logoAlt="X"
            description="Connect reporting data to Cphere AI for cross platform analytics."
          />

          <ProviderCard
            providerKey="GOOGLE"
            logoSrc={googleLogo}
            logoAlt="Google & YouTube"
            description="Connect reporting data to Cphere AI for cross platform analytics."
          />

          <ProviderCard
            providerKey="REDDIT"
            logoSrc={redditLogo}
            logoAlt="Reddit"
            description="Connect Reddit reporting data to Cphere AI for cross-platform analytics."
          />

          <DerivedCard
            title={integrationStatus.RUMBLE.label}
            providerKey="RUMBLE"
            logoSrc={rumbleLogo}
            logoAlt="Rumble"
            isConnected={rumbleConnected}
            description="Ads reporting status for cross-platform analytics."
          />

          <DerivedCard
            title={integrationStatus.TRUTH_SOCIAL.label}
            providerKey="TRUTH_SOCIAL"
            logoSrc={truthSocialLogo}
            logoAlt="Truth Social"
            isConnected={truthSocialConnected}
            description="Ads reporting status for cross-platform analytics."
          />

          <ProviderCard
            providerKey="AMAZON"
            logoSrc={amazonLogo}
            logoAlt="Amazon"
            description="Connect Amazon Ads OAuth and map the advertiser profile so Cphere can validate approval readiness and reporting access."
          />

          <YahooDspIntegrationCard apiBase={apiBase} advertiserId={selectedAdvertiserId} />

          <StackAdaptCard />

          <ProviderCard
            providerKey="GOOGLE_ANALYTICS"
            logoSrc={googleAnalyticsLogo}
            logoAlt="Google Analytics"
            description="Connect Google Analytics / GA4 reporting to Cphere AI for website and conversion analysis."
          />

          <DerivedCard
            title="CinemaCloudWorks"
            providerKey="CINEMACLOUDWORKS"
            logoSrc={purpleFilmIcon}
            logoAlt="CinemaCloudWorks"
            isConnected={cinemaCloudWorksConnected}
            description="Cinema reporting status for campaign analytics."
          />

          <DerivedCard
            title="Sherloc"
            providerKey="SHERLOC"
            logoSrc={accountTileImage}
            logoAlt="Sherloc"
            isConnected={sherlocConnected}
            description="AI Engine capability status for Sherloc Agentic workflows."
          />

          <DerivedCard
            title="Eden"
            providerKey="EDEN"
            logoSrc={profileSettingsImage}
            logoAlt="Eden"
            isConnected={edenConnected}
            description="AI Engine capability status for Eden COPILOT workflows."
          />

          <DerivedCard
            title="Quantum"
            providerKey="QUANTUM"
            logoSrc={aiOptimizationsImage}
            logoAlt="Quantum memories"
            isConnected={quantumMemoryConnected}
            description="Memories capability status for persistent Eden campaign context."
          />

          <DerivedCard
            title="Creative Studio+"
            providerKey="DROPBOX"
            logoSrc={purpleMarbleImage}
            logoAlt="Creative Studio+"
            isConnected={dropboxConnected}
            description="Creative Studio+ storage status for shared asset workflows."
          />

          <DerivedCard
            title="Creative Studio"
            providerKey="GOOGLE_DRIVE"
            logoSrc={creativeStudioImage}
            logoAlt="Creative Studio"
            isConnected={googleDriveCreativeStudioConnected}
            description="Creative Studio storage status for shared assets."
          />

          <DerivedCard
            title="Metricool"
            providerKey="METRICOOL"
            logoSrc={metricoolLogo}
            logoAlt="Metricool"
            isConnected={metricoolConnected}
            description="Social analytics connection status for reporting."
          />

          <ProviderCard
            providerKey="SHOPIFY"
            logoSrc={shopifyLogo}
            logoAlt="Shopify"
            description="Read-only commerce reporting for orders, revenue, products, customers, inventory, discounts, and store readiness."
          />

          <ProviderCard
            providerKey="LINKEDIN"
            logoSrc={linkedinLogo}
            logoAlt="LinkedIn"
            description="Read-only LinkedIn Ads reporting, account visibility, campaign discovery, creative discovery, and analytics."
          />
          </div>
        ) : null}

        {loadingIntegrations && (
          <div className="integrations-loading-message">
            LOADING INTEGRATION STATUS...
          </div>
        )}

        {disconnectModalOpen && (
          <div className="cphere-modal-overlay" onClick={closeDisconnectModal}>
            <div className="cphere-modal" onClick={(e) => e.stopPropagation()}>
              <div className="cphere-modal-header">
                <div className="cphere-modal-title">
                  DISCONNECT {providerPrettyName(pendingDisconnectProvider)}?
                </div>
                <button className="cphere-modal-x" onClick={closeDisconnectModal}>
                  ✕
                </button>
              </div>

              <div className="cphere-modal-body">
                <p className="cphere-modal-copy">
                  This will remove the active connection and may stop reporting until
                  you reconnect.
                </p>
              </div>

              <div className="cphere-modal-actions">
                <button
                  className="cphere-btn cphere-btn-secondary"
                  onClick={closeDisconnectModal}
                >
                  CANCEL
                </button>
                <button
                  className="cphere-btn cphere-btn-danger"
                  onClick={performDisconnect}
                >
                  DISCONNECT
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Integrations;
